import React, { useState, useEffect, useCallback } from "react";
import { jsPDF } from "jspdf";
import { ResponsiveContainer, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, LineChart, Line, PieChart, Pie, Cell, AreaChart, Area } from "recharts";
import * as api from "../api";
import { STATUS_COLORS, ROLE_LABELS, ROLE_COLORS, MANAGER_PERMISSIONS, DEFAULT_PERMISSIONS, PERMISSION_PRESETS, getPermissions } from "../constants";
import { C, R, SH, F, numeric, monoRef, CHART } from "../theme";
import { Icon, DataIcon, ARTICLE_ICONS, resolveIcon } from "../icons";
import {
  Btn, IconBtn, Badge, Chip, Card, SectionHeader, Input, Select, Textarea,
  Toggle, Spinner, EmptyState, Stat, Ref, Modal, Callout,
} from "./UI";
import { DEFAULT_RECEIPT_SIZES, RECEIPT_SIZE_LABELS, setReceiptSizesCache } from "../utils";
import Comptabilite from "./Comptabilite";
import MFASetup from "./MFASetup";

// ─── Sélecteur d'icônes ───────────────────────────────────────────────────────
// Remplace les anciennes palettes d'emojis (articles, services, catégories).
// La valeur retenue est un nom sémantique du registre src/icons.js.
function IconPicker({ label = "Icône", value, onChange }) {
  const current = resolveIcon(value, ARTICLE_ICONS[0].name);
  const currentLabel = (ARTICLE_ICONS.find(o => o.name === current) || {}).label;
  return (
    <div style={{ marginBottom: 14 }}>
      <label style={{ display: "block", fontWeight: F.weight.medium, marginBottom: 5, fontSize: F.size.base, color: C.inkBody }}>
        {label}
      </label>
      <div
        role="radiogroup"
        aria-label={label}
        style={{
          display: "flex", flexWrap: "wrap", gap: 6, padding: 10,
          background: C.surfaceAlt, border: `1px solid ${C.border}`, borderRadius: R.md,
          maxHeight: 160, overflowY: "auto",
        }}
      >
        {ARTICLE_ICONS.map(({ name, label: optLabel }) => {
          const selected = current === name;
          return (
            <button
              key={name}
              type="button"
              title={optLabel}
              aria-label={optLabel}
              aria-pressed={selected}
              onClick={() => onChange(name)}
              style={{
                width: 34, height: 34, borderRadius: R.md,
                border: `1px solid ${selected ? C.navy : C.border}`,
                background: selected ? C.navySoft : C.surface,
                color: selected ? C.navy : C.inkMuted,
                display: "flex", alignItems: "center", justifyContent: "center",
                cursor: "pointer",
              }}
            >
              <Icon name={name} size={16} />
            </button>
          );
        })}
      </div>
      <div className="icon-text" style={{ marginTop: 6, fontSize: F.size.sm, color: C.inkMuted }}>
        Sélection :
        <Icon name={current} size={14} color={C.ink} />
        <span style={{ color: C.ink, fontWeight: F.weight.medium }}>{currentLabel || current}</span>
      </div>
    </div>
  );
}

function Admin({ setView, user }) {
  const [tab, setTab] = useState("dashboard");
  const [revenuePeriod, setRevenuePeriod] = useState("daily"); // daily | weekly | monthly
  const [referralStats, setReferralStats] = useState(null);
  const [referralLoading, setReferralLoading] = useState(false);
  const [visitorsStats, setVisitorsStats] = useState(null);

  // Users
  const [users, setUsers] = useState([]);
  const [uLoading, setULoading] = useState(true);
  const [uError, setUError] = useState("");
  const [showUForm, setShowUForm] = useState(false);
  const [editUser, setEditUser] = useState(null);
  const [uLogin, setULogin] = useState("");
  const [uPass, setUPass] = useState("");
  const [uName, setUName] = useState("");
  const [uRole, setURole] = useState("collector");
  const [uEtabs, setUEtabs] = useState([]);
  const [uPerms, setUPerms] = useState({ ...DEFAULT_PERMISSIONS });
  const [uSaving, setUSaving] = useState(false);

  // Services
  const [services, setServices] = useState([]);
  const [sLoading, setSLoading] = useState(true);
  const [sError, setSError] = useState("");
  const [showSForm, setShowSForm] = useState(false);
  const [editSvc, setEditSvc] = useState(null);
  const [sIcon, setSIcon] = useState("garment");
  const [sLabel, setSLabel] = useState("");
  const [sDesc, setSDesc] = useState("");
  const [sActive, setSActive] = useState(true);
  const [sPriceDisplay, setSPriceDisplay] = useState("");
  const [sSaving, setSSaving] = useState(false);

  // Pricing
  const [pricing, setPricing] = useState([]);
  const [pLoading, setPLoading] = useState(true);
  const [pError, setPError] = useState("");
  const [showPForm, setShowPForm] = useState(false);
  const [editPrc, setEditPrc] = useState(null);
  const [pTitle, setPTitle] = useState("");
  const [pPrice, setPPrice] = useState("");
  const [pDesc, setPDesc] = useState("");
  const [pPopular, setPPopular] = useState(false);
  const [pActive, setPActive] = useState(true);
  const [pSaving, setPSaving] = useState(false);

  // Logs
  const [logs, setLogs] = useState([]);
  const [logsLoading, setLogsLoading] = useState(false);
  const [logsPage, setLogsPage] = useState(1);

  // Notifications
  const [notifs, setNotifs] = useState([]);
  const [notifsLoading, setNotifsLoading] = useState(false);

  // Backup
  const [backupLoading, setBackupLoading] = useState(false);

  // WhatsApp settings
  // ─── SMS MTarget ───────────────────────────────────────────────────────
  const [mtUser, setMtUser] = useState("");
  const [mtPass, setMtPass] = useState("");
  const [mtPassSet, setMtPassSet] = useState(false);
  const [mtSender, setMtSender] = useState("SetalExpr");
  const [mtServiceId, setMtServiceId] = useState("");
  const [mtApiUrl, setMtApiUrl] = useState("");
  const [mtSaving, setMtSaving] = useState(false);
  const [mtTestPhone, setMtTestPhone] = useState("");
  const [mtTesting, setMtTesting] = useState(false);
  const [mtResult, setMtResult] = useState(null);

  const [waPhoneId, setWaPhoneId] = useState("");
  const [waToken, setWaToken] = useState("");
  const [waTestPhone, setWaTestPhone] = useState("");
  const [waSaving, setWaSaving] = useState(false);
  const [waTesting, setWaTesting] = useState(false);
  const [waTestResult, setWaTestResult] = useState(null);
  const [broadcastMsg, setBroadcastMsg] = useState("");
  const [broadcastClients, setBroadcastClients] = useState([]);
  const [broadcastSelected, setBroadcastSelected] = useState([]);
  const [broadcastSelectAll, setBroadcastSelectAll] = useState(false);
  const [broadcastSending, setBroadcastSending] = useState(false);
  const [broadcastResult, setBroadcastResult] = useState(null);
  const [broadcastLoading, setBroadcastLoading] = useState(false);

  // Receipt format
  const [receiptFormat, setReceiptFormat] = useState("a5");

  // Branding
  const [brandSlogan, setBrandSlogan] = useState("Le pressing vient vers vous.");
  const [brandLogoUrl, setBrandLogoUrl] = useState("");
  const [brandSaving, setBrandSaving] = useState(false);
  const [phoneMain, setPhoneMain] = useState("+221 76 809 09 28");
  const [phoneWhatsapp, setPhoneWhatsapp] = useState("221768090928");
  const [phonesExtra, setPhonesExtra] = useState("");

  // Receipt font sizes
  const [receiptSizes, setReceiptSizes] = useState(null);
  const [receiptSizesSaving, setReceiptSizesSaving] = useState(false);

  // Notification messages config
  const [notifMessages, setNotifMessages] = useState({});
  const [notifMsgSaving, setNotifMsgSaving] = useState(false);

  // Etablissements
  const [etabs, setEtabs] = useState([]);
  const [etabLoading, setEtabLoading] = useState(false);
  const [showEtabForm, setShowEtabForm] = useState(false);
  const [editEtab, setEditEtab] = useState(null);
  const [etabName, setEtabName] = useState("");
  const [etabType, setEtabType] = useState("pressing");
  const [etabAddress, setEtabAddress] = useState("");
  const [etabPhone, setEtabPhone] = useState("");
  const [etabEmail, setEtabEmail] = useState("");
  const [etabSaving, setEtabSaving] = useState(false);

  // Expense Categories
  const [expCats, setExpCats] = useState([]);
  const [expCatLoading, setExpCatLoading] = useState(false);
  const [showExpCatForm, setShowExpCatForm] = useState(false);
  const [editExpCat, setEditExpCat] = useState(null);
  const [ecName, setEcName] = useState("");
  const [ecIcon, setEcIcon] = useState("package");
  const [ecSaving, setEcSaving] = useState(false);

  // Articles
  const [artList, setArtList] = useState([]);
  const [artLoading, setArtLoading] = useState(false);
  const [showArtForm, setShowArtForm] = useState(false);
  const [editArt, setEditArt] = useState(null);
  const [artName, setArtName] = useState("");
  const [artPrice, setArtPrice] = useState("");
  const [artIcon, setArtIcon] = useState("garment");
  const [artActive, setArtActive] = useState(true);
  const [artShowLanding, setArtShowLanding] = useState(false);
  const [artDisplayName, setArtDisplayName] = useState("");
  const [artSaving, setArtSaving] = useState(false);

  // Clients
  const [clientsList, setClientsList] = useState([]);
  const [clientsLoading, setClientsLoading] = useState(false);
  const [clientSearch, setClientSearch] = useState("");
  const [clientDetail, setClientDetail] = useState(null);
  const [clientOrders, setClientOrders] = useState([]);
  const [clientOrdersLoading, setClientOrdersLoading] = useState(false);
  const [clientSort, setClientSort] = useState("total_orders");

  // Loyalty config
  const [loyaltyConfig, setLoyaltyConfig] = useState({ enabled: true, thresholds: [{ points: 10, discount: 10 }, { points: 25, discount: 20 }, { points: 50, discount: 30 }, { points: 100, discount: 40 }] });
  const [loyaltyLoading, setLoyaltyLoading] = useState(false);
  const [loyaltySaving, setLoyaltySaving] = useState(false);

  // WhatsApp Inbox
  const [waInbox, setWaInbox] = useState([]);
  const [waInboxLoading, setWaInboxLoading] = useState(false);
  const [waConvo, setWaConvo] = useState(null); // { phone, messages, client }
  const [waConvoLoading, setWaConvoLoading] = useState(false);
  const [waReplyText, setWaReplyText] = useState("");
  const [waReplySending, setWaReplySending] = useState(false);
  const [waUnread, setWaUnread] = useState(0);

  // Admin Dashboard
  const [adminStats, setAdminStats] = useState(null);
  const [adminStatsLoading, setAdminStatsLoading] = useState(false);

  // Promo codes
  const [promoCodes, setPromoCodes] = useState([]);
  const [promoLoading, setPromoLoading] = useState(false);
  const [promoConfig, setPromoConfig] = useState({ promo_enabled: true, referral_enabled: true, welcome_enabled: true, welcome_message: "", welcome_promo: "BIENVENUE30", referral_reward: 2000 });
  const [promoConfigLoading, setPromoConfigLoading] = useState(false);
  const [promoSaving, setPromoSaving] = useState(false);
  const [showPromoForm, setShowPromoForm] = useState(false);
  const [editPromo, setEditPromo] = useState(null);
  const [pCode, setPCode] = useState("");
  const [pType, setPType] = useState("percentage");
  const [pValue, setPValue] = useState("");
  const [pMaxUses, setPMaxUses] = useState("");
  const [pMinAmount, setPMinAmount] = useState("");
  const [pExpires, setPExpires] = useState("");
  const [promoActive, setPromoActive] = useState(true);
  const [promoFormSaving, setPromoFormSaving] = useState(false);

  // Weekly Report
  const [reportLoading, setReportLoading] = useState(false);

  const loadArticles = useCallback(async () => {
    setArtLoading(true);
    try { setArtList(await api.adminGetArticles()); } catch {}
    finally { setArtLoading(false); }
  }, []);

  const loadEtabs = useCallback(async () => {
    setEtabLoading(true);
    try { setEtabs(await api.adminGetEtablissements()); } catch {}
    finally { setEtabLoading(false); }
  }, []);
  const loadExpCats = useCallback(async () => {
    setExpCatLoading(true);
    try { setExpCats(await api.adminGetExpenseCategories()); } catch {}
    finally { setExpCatLoading(false); }
  }, []);

  const loadUsers = useCallback(async () => {
    setULoading(true); setUError("");
    try { setUsers(await api.adminGetUsers()); } catch (e) { setUError(e.message); } finally { setULoading(false); }
  }, []);
  const loadServices = useCallback(async () => {
    setSLoading(true); setSError("");
    try { setServices(await api.adminGetServices()); } catch (e) { setSError(e.message); } finally { setSLoading(false); }
  }, []);
  const loadPricing = useCallback(async () => {
    setPLoading(true); setPError("");
    try { setPricing(await api.adminGetPricing()); } catch (e) { setPError(e.message); } finally { setPLoading(false); }
  }, []);

  const loadSettings = useCallback(async () => {
    try {
      const s = await api.getSettings();
      if (s.wa_phone_id) setWaPhoneId(s.wa_phone_id);
      if (s.wa_token) setWaToken(s.wa_token);
      if (s.receipt_format) setReceiptFormat(s.receipt_format);
      if (s.slogan) setBrandSlogan(s.slogan);
      if (s.logo_url) setBrandLogoUrl(s.logo_url);
      if (s.receipt_font_sizes) {
        try { setReceiptSizes(JSON.parse(s.receipt_font_sizes)); } catch {}
      }
      if (s.phone_main) setPhoneMain(s.phone_main);
      if (s.phone_whatsapp) setPhoneWhatsapp(s.phone_whatsapp);
      if (s.phones_extra) setPhonesExtra(s.phones_extra);
      // MTarget : le serveur renvoie un masque, jamais le mot de passe réel.
      if (s.mtarget_username) setMtUser(s.mtarget_username);
      if (s.mtarget_sender) setMtSender(s.mtarget_sender);
      if (s.mtarget_service_id) setMtServiceId(s.mtarget_service_id);
      if (s.mtarget_api_url) setMtApiUrl(s.mtarget_api_url);
      setMtPassSet(!!s.mtarget_password_set);
      setMtPass(s.mtarget_password_set ? "••••••••" : "");
    } catch {}
  }, []);

  // Load unread count on mount
  useEffect(() => { api.getWaUnreadCount().then(d => setWaUnread(d.unread || 0)).catch(() => {}); }, []);

  // Lazy load: only fetch data when its tab is active
  useEffect(() => {
    if (tab === "dashboard") {
      setAdminStatsLoading(true);
      api.getStats().then(d => setAdminStats(d)).catch(() => {}).finally(() => setAdminStatsLoading(false));
      api.getVisitorsStats().then(d => setVisitorsStats(d)).catch(() => {});
    }
    if (tab === "inbox") {
      setWaInboxLoading(true);
      api.getWaInbox().then(d => { setWaInbox(d || []); setWaUnread(0); }).catch(() => {}).finally(() => setWaInboxLoading(false));
    }
    if (tab === "users") loadUsers();
    if (tab === "services") loadServices();
    if (tab === "pricing") loadPricing();
    if (tab === "etabs") loadEtabs();
    if (tab === "categories" || tab === "comptabilite") loadExpCats();
    if (tab === "comptabilite" && etabs.length === 0) loadEtabs();
    if (tab === "articles") loadArticles();
    if (tab === "clients") {
      setClientsLoading(true);
      api.adminGetClients().then(d => setClientsList(d || [])).catch(() => {}).finally(() => setClientsLoading(false));
    }
    if (tab === "whatsapp" || tab === "receipt" || tab === "branding" || tab === "sms") loadSettings();
    if (tab === "whatsapp") {
      setBroadcastLoading(true);
      api.adminGetClients().then(data => {
        const clients = (data || []).filter(c => c.phone);
        setBroadcastClients(clients);
      }).catch(() => {}).finally(() => setBroadcastLoading(false));
    }
    if (tab === "loyalty") {
      setLoyaltyLoading(true);
      api.getLoyaltyConfig().then(c => setLoyaltyConfig(c)).catch(() => {}).finally(() => setLoyaltyLoading(false));
    }
    if (tab === "parrainage") {
      setReferralLoading(true);
      Promise.all([
        api.getReferralStats().catch(() => null),
        api.getPromoConfig().catch(() => null),
      ]).then(([stats, cfg]) => {
        setReferralStats(stats);
        if (cfg) setPromoConfig(cfg);
      }).finally(() => setReferralLoading(false));
    }
    if (tab === "promo") {
      setPromoLoading(true);
      setPromoConfigLoading(true);
      api.adminGetPromoCodes().then(d => setPromoCodes(d || [])).catch(() => {}).finally(() => setPromoLoading(false));
      api.getPromoConfig().then(c => setPromoConfig(pc => ({ ...pc, ...c }))).catch(() => {}).finally(() => setPromoConfigLoading(false));
    }
    if (tab === "horaires") { loadSlots(); loadDSlots(); }
    if (tab === "logs") {
      setLogsLoading(true);
      api.getAuditLogs(logsPage, 50).then(d => setLogs(d.logs || d || [])).catch(() => {}).finally(() => setLogsLoading(false));
    }
    if (tab === "notifs") {
      setNotifsLoading(true);
      api.getNotifications().then(d => setNotifs(d || [])).catch(() => {}).finally(() => setNotifsLoading(false));
      // Load notification messages config
      api.getSettings().then(s => {
        if (s && s.notification_messages) {
          try { setNotifMessages(JSON.parse(s.notification_messages)); } catch {}
        }
      }).catch(() => {});
    }
    // Etabs needed for user form (establishments assignment)
    if (tab === "users" && etabs.length === 0) loadEtabs();
  }, [tab, logsPage]); // eslint-disable-line

  function openUserForm(u = null) {
    setEditUser(u);
    setULogin(u?.login || ""); setUPass(""); setUName(u?.name || ""); setURole(u?.role || "collector");
    setUEtabs(u?.establishments ? u.establishments.map(e => e.id) : []);
    setUPerms(u?.permissions ? { ...DEFAULT_PERMISSIONS, ...u.permissions } : { ...DEFAULT_PERMISSIONS });
    setShowUForm(true);
  }
  function openSvcForm(s = null) {
    setEditSvc(s);
    setSIcon(resolveIcon(s?.icon, "garment")); setSLabel(s?.label || ""); setSDesc(s?.description || ""); setSActive(s?.active !== false);
    setSPriceDisplay(s?.price_display || "");
    setShowSForm(true);
  }
  function openPrcForm(p = null) {
    setEditPrc(p);
    setPTitle(p?.title || ""); setPPrice(p?.price || ""); setPDesc(p?.description || "");
    setPPopular(p?.popular || false); setPActive(p?.active !== false);
    setShowPForm(true);
  }

  async function submitUser(e) {
    e.preventDefault(); setUSaving(true);
    try {
      let userId;
      if (editUser) {
        const data = { name: uName, role: uRole };
        if (uPass) data.pass = uPass;
        if (uRole === "manager") data.permissions = uPerms;
        await api.adminUpdateUser(editUser.id, data);
        userId = editUser.id;
      } else {
        const createData = { login: uLogin, pass: uPass, name: uName, role: uRole };
        if (uRole === "manager") createData.permissions = uPerms;
        const created = await api.adminCreateUser(createData);
        userId = created.id;
      }
      // Save establishment assignments
      if (userId && (uRole === "manager" || uRole === "collector")) {
        await api.setUserEstablishments(userId, uEtabs).catch(() => {});
      }
      setShowUForm(false); loadUsers();
    } catch (err) { alert("Erreur : " + err.message); } finally { setUSaving(false); }
  }
  async function toggleUserActive(u) {
    try { await api.adminUpdateUser(u.id, { active: !u.active }); loadUsers(); } catch (err) { alert("Erreur : " + err.message); }
  }
  async function deleteUser(u) {
    if (!window.confirm(`Supprimer "${u.name}" ?`)) return;
    try { await api.adminDeleteUser(u.id); loadUsers(); } catch (err) { alert("Erreur : " + err.message); }
  }
  async function submitSvc(e) {
    e.preventDefault(); setSSaving(true);
    try {
      if (editSvc) await api.adminUpdateService(editSvc.id, { icon: sIcon, label: sLabel, description: sDesc, active: sActive, price_display: sPriceDisplay });
      else await api.adminCreateService({ icon: sIcon, label: sLabel, description: sDesc, active: sActive, price_display: sPriceDisplay });
      setShowSForm(false); loadServices();
    } catch (err) { alert("Erreur : " + err.message); } finally { setSSaving(false); }
  }
  async function deleteSvc(s) {
    if (!window.confirm(`Supprimer "${s.label}" ?`)) return;
    try { await api.adminDeleteService(s.id); loadServices(); } catch (err) { alert("Erreur : " + err.message); }
  }
  async function submitPrc(e) {
    e.preventDefault(); setPSaving(true);
    try {
      if (editPrc) await api.adminUpdatePricing(editPrc.id, { title: pTitle, price: pPrice, description: pDesc, popular: pPopular, active: pActive });
      else await api.adminCreatePricing({ title: pTitle, price: pPrice, description: pDesc, popular: pPopular, active: pActive });
      setShowPForm(false); loadPricing();
    } catch (err) { alert("Erreur : " + err.message); } finally { setPSaving(false); }
  }
  async function deletePrc(p) {
    if (!window.confirm(`Supprimer "${p.title}" ?`)) return;
    try { await api.adminDeletePricing(p.id); loadPricing(); } catch (err) { alert("Erreur : " + err.message); }
  }

  async function handleBackup() {
    setBackupLoading(true);
    try {
      const data = await api.createBackup();
      const blob = new Blob([JSON.stringify(data, null, 2)], { type: "application/json" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url; a.download = `setal-backup-${new Date().toISOString().split("T")[0]}.json`;
      document.body.appendChild(a); a.click(); document.body.removeChild(a);
      URL.revokeObjectURL(url);
      alert("Sauvegarde téléchargée.");
    } catch (err) { alert("Erreur : " + err.message); }
    finally { setBackupLoading(false); }
  }

  // ── Etablissement form handlers ──
  function openEtabForm(e = null) {
    setEditEtab(e);
    setEtabName(e?.name || ""); setEtabType(e?.type || "pressing");
    setEtabAddress(e?.address || ""); setEtabPhone(e?.phone || ""); setEtabEmail(e?.email || "");
    setShowEtabForm(true);
  }
  async function submitEtab(ev) {
    ev.preventDefault(); setEtabSaving(true);
    try {
      const data = { name: etabName, type: etabType, address: etabAddress, phone: etabPhone, email: etabEmail };
      if (editEtab) await api.adminUpdateEtablissement(editEtab.id, data);
      else await api.adminCreateEtablissement(data);
      setShowEtabForm(false); loadEtabs();
    } catch (err) { alert("Erreur : " + err.message); }
    finally { setEtabSaving(false); }
  }
  async function toggleEtabActive(e) {
    try { await api.adminUpdateEtablissement(e.id, { active: !e.active }); loadEtabs(); } catch (err) { alert("Erreur : " + err.message); }
  }
  async function deleteEtab(e) {
    if (!window.confirm(`Désactiver "${e.name}" ?`)) return;
    try { await api.adminDeleteEtablissement(e.id); loadEtabs(); } catch (err) { alert("Erreur : " + err.message); }
  }

  // ── Expense category form handlers ──
  function openExpCatForm(c = null) {
    setEditExpCat(c);
    setEcName(c?.name || ""); setEcIcon(resolveIcon(c?.icon, "package"));
    setShowExpCatForm(true);
  }
  async function submitExpCat(ev) {
    ev.preventDefault(); setEcSaving(true);
    try {
      if (editExpCat) await api.adminUpdateExpenseCategory(editExpCat.id, { name: ecName, icon: ecIcon });
      else await api.adminCreateExpenseCategory({ name: ecName, icon: ecIcon });
      setShowExpCatForm(false); loadExpCats();
    } catch (err) { alert("Erreur : " + err.message); }
    finally { setEcSaving(false); }
  }
  async function deleteExpCat(c) {
    if (!window.confirm(`Supprimer "${c.name}" ?`)) return;
    try { await api.adminDeleteExpenseCategory(c.id); loadExpCats(); } catch (err) { alert("Erreur : " + err.message); }
  }

  // ── Article form handlers ──
  function openArtForm(a = null) {
    setEditArt(a);
    setArtName(a?.name || ""); setArtPrice(a?.price?.toString() || "");
    setArtIcon(resolveIcon(a?.icon, "garment")); setArtActive(a?.active !== false);
    setArtShowLanding(a?.show_on_landing || false);
    setArtDisplayName(a?.display_name || "");
    setShowArtForm(true);
  }
  async function submitArt(ev) {
    ev.preventDefault(); setArtSaving(true);
    try {
      const data = { name: artName, price: parseFloat(artPrice), icon: artIcon, active: artActive, show_on_landing: artShowLanding, display_name: artDisplayName };
      if (editArt) await api.adminUpdateArticle(editArt.id, data);
      else await api.adminCreateArticle(data);
      setShowArtForm(false); loadArticles();
    } catch (err) { alert("Erreur : " + err.message); }
    finally { setArtSaving(false); }
  }
  async function deleteArt(a) {
    if (!window.confirm(`Supprimer "${a.name}" ?`)) return;
    try { await api.adminDeleteArticle(a.id); loadArticles(); } catch (err) { alert("Erreur : " + err.message); }
  }

  // ── Collection Slots ──
  const [slots, setSlots] = useState([]);
  const [slotsLoading, setSlotsLoading] = useState(false);
  const [showSlotForm, setShowSlotForm] = useState(false);
  const [editSlot, setEditSlot] = useState(null);
  const [slotDay, setSlotDay] = useState(1);
  const [slotStart, setSlotStart] = useState("08:00");
  const [slotEnd, setSlotEnd] = useState("10:00");
  const [slotSaving, setSlotSaving] = useState(false);

  // Delivery slots state
  const [dSlots, setDSlots] = useState([]);
  const [dSlotsLoading, setDSlotsLoading] = useState(false);
  const [deliveryFeesConfig, setDeliveryFeesConfig] = useState({ enabled: true, campus_fee: 500, hors_campus_fee: 1000, free_threshold: 5000 });
  const [dfSaving, setDfSaving] = useState(false);
  const [showDSlotForm, setShowDSlotForm] = useState(false);
  const [editDSlot, setEditDSlot] = useState(null);
  const [dSlotDay, setDSlotDay] = useState(1);
  const [dSlotStart, setDSlotStart] = useState("08:00");
  const [dSlotEnd, setDSlotEnd] = useState("10:00");
  const [dSlotSaving, setDSlotSaving] = useState(false);

  const DAYS_FR = ["Dimanche","Lundi","Mardi","Mercredi","Jeudi","Vendredi","Samedi"];

  const loadSlots = useCallback(async () => {
    setSlotsLoading(true);
    try { setSlots(await api.adminGetCollectionSlots()); } catch {}
    finally { setSlotsLoading(false); }
  }, []);

  useEffect(() => {
    if (tab === "horaires") loadSlots();
  }, [tab, loadSlots]);

  function openSlotForm(s = null) {
    setEditSlot(s);
    setSlotDay(s?.day_of_week ?? 1);
    setSlotStart(s?.start_time || "08:00");
    setSlotEnd(s?.end_time || "10:00");
    setShowSlotForm(true);
  }
  async function submitSlot(ev) {
    ev.preventDefault(); setSlotSaving(true);
    try {
      const data = { day_of_week: parseInt(slotDay), start_time: slotStart, end_time: slotEnd };
      if (editSlot) await api.adminUpdateCollectionSlot(editSlot.id, data);
      else await api.adminCreateCollectionSlot(data);
      setShowSlotForm(false); loadSlots();
    } catch (err) { alert("Erreur : " + err.message); }
    finally { setSlotSaving(false); }
  }
  async function deleteSlot(s) {
    if (!window.confirm("Supprimer ce créneau ?")) return;
    try { await api.adminDeleteCollectionSlot(s.id); loadSlots(); } catch (err) { alert("Erreur : " + err.message); }
  }
  async function toggleSlotActive(s) {
    try { await api.adminUpdateCollectionSlot(s.id, { active: !s.active }); loadSlots(); } catch (err) { alert("Erreur : " + err.message); }
  }

  // Group slots by day
  const slotsByDay = {};
  slots.forEach(s => {
    if (!slotsByDay[s.day_of_week]) slotsByDay[s.day_of_week] = [];
    slotsByDay[s.day_of_week].push(s);
  });

  // ── Delivery slots logic ──
  const loadDSlots = useCallback(async () => {
    setDSlotsLoading(true);
    try { setDSlots(await api.adminGetDeliverySlots()); } catch {}
    finally { setDSlotsLoading(false); }
  }, []);

  useEffect(() => {
    if (tab === "livraison") { loadDSlots(); api.getDeliveryFeesConfig().then(c => setDeliveryFeesConfig(c)).catch(() => {}); }
  }, [tab, loadDSlots]);

  function openDSlotForm(s = null) {
    setEditDSlot(s);
    setDSlotDay(s?.day_of_week ?? 1);
    setDSlotStart(s?.start_time || "08:00");
    setDSlotEnd(s?.end_time || "10:00");
    setShowDSlotForm(true);
  }
  async function submitDSlot(ev) {
    ev.preventDefault(); setDSlotSaving(true);
    try {
      const data = { day_of_week: parseInt(dSlotDay), start_time: dSlotStart, end_time: dSlotEnd };
      if (editDSlot) await api.adminUpdateDeliverySlot(editDSlot.id, data);
      else await api.adminCreateDeliverySlot(data);
      setShowDSlotForm(false); loadDSlots();
    } catch (err) { alert("Erreur : " + err.message); }
    finally { setDSlotSaving(false); }
  }
  async function deleteDSlot(s) {
    if (!window.confirm("Supprimer ce créneau de livraison ?")) return;
    try { await api.adminDeleteDeliverySlot(s.id); loadDSlots(); } catch (err) { alert("Erreur : " + err.message); }
  }
  async function toggleDSlotActive(s) {
    try { await api.adminUpdateDeliverySlot(s.id, { active: !s.active }); loadDSlots(); } catch (err) { alert("Erreur : " + err.message); }
  }

  const dSlotsByDay = {};
  dSlots.forEach(s => {
    if (!dSlotsByDay[s.day_of_week]) dSlotsByDay[s.day_of_week] = [];
    dSlotsByDay[s.day_of_week].push(s);
  });

  async function generateWeeklyReport() {
    setReportLoading(true);
    try {
      const data = await api.getWeeklyReport();
      const doc = new jsPDF({ unit: "mm", format: "a4" });
      const w = doc.internal.pageSize.getWidth();
      let y = 15;
      doc.setFillColor(15, 45, 110);
      doc.rect(0, 0, w, 35, "F");
      doc.setTextColor(255, 255, 255);
      doc.setFontSize(22); doc.setFont(undefined, "bold");
      doc.text("Setal Express", w/2, 18, { align: "center" });
      doc.setFontSize(12); doc.setFont(undefined, "normal");
      doc.text(`Rapport Hebdomadaire \u2014 ${data.period.from} au ${data.period.to}`, w/2, 28, { align: "center" });
      y = 45;
      doc.setTextColor(55, 65, 81); doc.setFontSize(14); doc.setFont(undefined, "bold");
      doc.text("Indicateurs cl\u00e9s", 15, y); y += 10;
      const kpis = [
        ["Commandes", data.orders.count.toString(), data.previousWeek.count > 0 ? `${((data.orders.count - data.previousWeek.count) / data.previousWeek.count * 100).toFixed(0)}%` : "\u2014"],
        ["Chiffre d'affaires", `${data.orders.revenue.toLocaleString()} FCFA`, data.previousWeek.revenue > 0 ? `${((data.orders.revenue - data.previousWeek.revenue) / data.previousWeek.revenue * 100).toFixed(0)}%` : "\u2014"],
        ["D\u00e9penses", `${data.expenses.toLocaleString()} FCFA`, ""],
        ["B\u00e9n\u00e9fice net", `${data.profit.toLocaleString()} FCFA`, ""],
        ["Nouveaux clients", data.newClients.toString(), ""],
        ["Note moyenne", data.reviews.count > 0 ? `${data.reviews.average.toFixed(1)}/5 (${data.reviews.count} avis)` : "Aucun avis", ""],
      ];
      doc.setFontSize(11);
      for (const [label, value, trend] of kpis) {
        doc.setFont(undefined, "normal"); doc.text(label, 20, y);
        doc.setFont(undefined, "bold"); doc.text(value, 110, y);
        if (trend) {
          const color = trend.startsWith("-") ? [220, 38, 38] : [22, 163, 74];
          doc.setTextColor(color[0], color[1], color[2]);
          doc.text(trend.startsWith("-") ? `\u2193 ${trend}` : `\u2191 ${trend}`, 170, y);
          doc.setTextColor(55, 65, 81);
        }
        y += 7;
      }
      y += 8;
      doc.setFontSize(14); doc.setFont(undefined, "bold");
      doc.text("R\u00e9partition par statut", 15, y); y += 8;
      doc.setFontSize(10); doc.setFont(undefined, "normal");
      for (const [status, count] of Object.entries(data.byStatus || {})) {
        doc.text(`${status}: ${count}`, 20, y); y += 5;
      }
      y += 8;
      if (data.topClients?.length) {
        doc.setFontSize(14); doc.setFont(undefined, "bold");
        doc.text("Top 5 Clients", 15, y); y += 8;
        doc.setFontSize(10); doc.setFont(undefined, "normal");
        data.topClients.forEach((c, i) => {
          doc.text(`${i+1}. ${c.client} \u2014 ${parseInt(c.orders)} cmd \u2014 ${parseFloat(c.spent).toLocaleString()} FCFA`, 20, y);
          y += 5;
        });
        y += 8;
      }
      if (data.topCollectors?.length) {
        doc.setFontSize(14); doc.setFont(undefined, "bold");
        doc.text("Performance Collecteurs", 15, y); y += 8;
        doc.setFontSize(10); doc.setFont(undefined, "normal");
        data.topCollectors.forEach((c, i) => {
          doc.text(`${i+1}. ${c.name} \u2014 ${c.orders} collectes`, 20, y);
          y += 5;
        });
      }
      const pageH = doc.internal.pageSize.getHeight();
      doc.setFontSize(8); doc.setTextColor(148, 163, 184);
      doc.text(`G\u00e9n\u00e9r\u00e9 le ${new Date().toLocaleDateString("fr-FR")} \u2014 Setal Express`, w/2, pageH - 10, { align: "center" });
      doc.save(`Rapport_Setal_${data.period.from}_${data.period.to}.pdf`);
    } catch (err) {
      alert("Erreur lors de la g\u00e9n\u00e9ration du rapport: " + err.message);
    } finally {
      setReportLoading(false);
    }
  }

  // Navigation latérale : 22 sections réparties en 6 familles.
  // Une liste verticale non groupée serait aussi peu lisible qu'une barre.
  const TAB_GROUPS = [
    ["Pilotage", [
      ["dashboard", "Tableau de bord", "dashboard"],
      ["comptabilite", "Comptabilité", "chart"],
    ]],
    ["Exploitation", [
      ["clients", "Clients", "user"],
      ["inbox", "Messages", "inbox"],
      ["sms", "SMS", "mobile"],
      ["whatsapp", "WhatsApp", "chat"],
      ["notifs", "Notifications", "bell"],
    ]],
    ["Catalogue", [
      ["articles", "Articles", "list"],
      ["services", "Services", "washing"],
      ["pricing", "Tarifs", "money"],
      ["categories", "Catégories", "layers"],
    ]],
    ["Organisation", [
      ["etabs", "Établissements", "building"],
      ["horaires", "Horaires", "clock"],
      ["livraison", "Livraison", "delivery"],
      ["users", "Utilisateurs", "users"],
    ]],
    ["Fidélisation", [
      ["loyalty", "Fidélité", "gift"],
      ["promo", "Promo", "ticket"],
      ["parrainage", "Parrainage", "handshake"],
    ]],
    ["Configuration", [
      ["branding", "Personnalisation", "palette"],
      ["receipt", "Reçus", "receipt"],
      ["mfa", "MFA", "shield"],
      ["logs", "Journal", "document"],
      ["backup", "Sauvegarde", "save"],
    ]],
  ];

  return (
    <div style={{ minHeight: "100vh", background: C.bg }}>
      <header style={{ background: C.surface, borderBottom: `1px solid ${C.border}` }}>
        <div style={{ padding: "14px 20px" }}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 12, flexWrap: "wrap" }}>
            <div style={{ minWidth: 0 }}>
              <div style={{ fontSize: F.size.sm, color: C.inkMuted, fontWeight: F.weight.medium, letterSpacing: ".04em", textTransform: "uppercase" }}>
                Espace administrateur
              </div>
              <h1 style={{ display: "flex", alignItems: "center", gap: 8, fontSize: F.size["2xl"], fontWeight: F.weight.semi, color: C.ink, letterSpacing: "-.02em", marginTop: 2 }}>
                <Icon name="settings" size={20} color={C.inkMuted} />
                Administration
              </h1>
              {user?.name && <p style={{ fontSize: F.size.base, color: C.inkMuted, marginTop: 2 }}>{user.name}</p>}
            </div>
            <div style={{ display: "flex", gap: 8, alignItems: "center", flexShrink: 0 }}>
              <Btn variant="ghost" small icon="download" loading={reportLoading} onClick={generateWeeklyReport}>
                {reportLoading ? "Génération…" : "Rapport hebdo"}
              </Btn>
              <Btn variant="white" small icon="logout" onClick={() => { api.logout(); setView("landing"); }}>Déconnexion</Btn>
            </div>
          </div>
        </div>
      </header>

      <div className="admin-shell">
        <nav className="admin-side" aria-label="Sections d'administration">
          {TAB_GROUPS.map(([groupLabel, items]) => (
            <div className="admin-group" key={groupLabel}>
              <div className="admin-group-label">{groupLabel}</div>
              {items.map(([k, label, icon]) => (
                <button
                  key={k}
                  type="button"
                  className={`admin-item ${tab === k ? "active" : ""}`}
                  aria-current={tab === k ? "page" : undefined}
                  onClick={() => setTab(k)}
                >
                  <Icon name={icon} size={15} strokeWidth={tab === k ? 2 : 1.5} />
                  {label}
                  {k === "inbox" && waUnread > 0 && (
                    <span className="admin-count" style={{
                      minWidth: 17, height: 17, padding: "0 5px", borderRadius: R.pill,
                      background: C.navy, color: C.inkOn, fontSize: F.size.xs, fontWeight: F.weight.semi,
                      display: "inline-flex", alignItems: "center", justifyContent: "center", ...numeric,
                    }}>{waUnread}</span>
                  )}
                </button>
              ))}
            </div>
          ))}
        </nav>

        <div className="admin-main"><div className="admin-inner">

        {/* Dashboard tab */}
        {tab === "dashboard" && (() => {
          const s = adminStats;
          const pct = (a, b) => b > 0 ? ((a - b) / b * 100).toFixed(0) : a > 0 ? "+100" : "0";
          const fk = v => v >= 1000000 ? `${(v/1000000).toFixed(1)}M` : v >= 1000 ? `${(v/1000).toFixed(0)}k` : v;
          const PAY_COLORS = { paye: C.success, avance: C.amber, non_paye: C.danger, pending: C.inkFaint };
          const PAY_LABELS = { paye: "Payé", avance: "Avance", non_paye: "Non payé", pending: "En attente" };
          const METHOD_LABELS = { cash: "Espèces", wave: "Wave", om: "Orange Money", card: "Carte" };
          const METHOD_COLORS = { cash: CHART[3], wave: CHART[1], om: CHART[2], card: CHART[4] };
          const TYPE_COLORS = { delivery: C.navy, sur_place: C.amber };
          const TYPE_LABELS = { delivery: "Collectes", sur_place: "Sur place" };

          return (
          <div style={{ marginTop: 8 }}>
            <SectionHeader icon="dashboard" title="Tableau de bord" subtitle="Vue d'ensemble de l'activité" />
            {adminStatsLoading ? <Spinner /> : s ? (
              <div>
                {/* ═══ INDICATEURS CLÉS ═══ */}
                <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(160px, 1fr))", gap: 12, marginBottom: 20 }}>
                  {[
                    ["money", "CA aujourd'hui", `${(s.today?.revenue || 0).toLocaleString()}`, "FCFA", "navy"],
                    ["package", "Commandes aujourd'hui", s.today?.orders || 0, "", "neutral"],
                    ["trendUp", "CA ce mois", `${(s.month?.revenue || 0).toLocaleString()}`, "FCFA", "navy"],
                    ["calendar", "Commandes ce mois", s.month?.orders || 0, "", "neutral"],
                    ["users", "Clients total", s.totalClients || 0, "", "neutral"],
                    ["cart", "Panier moyen", s.total?.orders ? `${Math.round(s.total.revenue / s.total.orders).toLocaleString()}` : "0", "FCFA", "neutral"],
                    ["heart", "Taux fidélisation", s.retention ? `${s.retention.rate}%` : "0%", "", "accent"],
                    ["repeat", "Clients fidèles", s.retention?.repeatClients || 0, `sur ${s.retention?.totalClients || 0}`, "success"],
                  ].map(([icon, label, value, unit, tone], i) => (
                    <Stat key={i} icon={icon} label={label} value={value} unit={unit} tone={tone} />
                  ))}
                </div>

                {/* ═══ VISITEURS DU SITE ═══ */}
                {visitorsStats && (
                  <Card style={{ marginBottom: 16 }}>
                    <SectionHeader
                      icon="show"
                      title="Visiteurs du site"
                      actions={<Chip tone="neutral">30 derniers jours</Chip>}
                    />

                    {/* KPI row */}
                    <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(140px, 1fr))", gap: 10, marginBottom: 14 }}>
                      {[
                        ["user", "Aujourd'hui", visitorsStats.today?.visitors || 0, `${visitorsStats.today?.views || 0} vues`, "neutral"],
                        ["calendar", "Hier", visitorsStats.yesterday?.visitors || 0, `${visitorsStats.yesterday?.views || 0} vues`, "neutral"],
                        ["calendarDays", "7 derniers jours", visitorsStats.week?.visitors || 0, `${visitorsStats.week?.views || 0} vues`, "neutral"],
                        ["chart", "30 derniers jours", visitorsStats.month?.visitors || 0, `${visitorsStats.month?.views || 0} vues`, "navy"],
                        ["globe", "Total", visitorsStats.total?.visitors || 0, `${visitorsStats.total?.views || 0} vues`, "neutral"],
                        ["target", "Conversion", `${visitorsStats.conversion?.rate || 0}%`, `${visitorsStats.conversion?.customers || 0} clients`, "success"],
                      ].map(([icon, label, value, sub, tone], i) => (
                        <Stat key={i} icon={icon} label={label} value={value} hint={sub} tone={tone} />
                      ))}
                    </div>

                    {/* Chart daily */}
                    {visitorsStats.daily && visitorsStats.daily.length > 0 && (
                      <div style={{ border: `1px solid ${C.border}`, borderRadius: R.lg, padding: 12, marginBottom: 12 }}>
                        <div style={{ display: "flex", alignItems: "center", gap: 7, fontWeight: F.weight.semi, fontSize: F.size.base, color: C.ink, marginBottom: 10 }}>
                          <Icon name="trendUp" size={15} color={C.inkMuted} />Évolution sur 30 jours
                        </div>
                        <div style={{ width: "100%", height: 180 }}>
                          <ResponsiveContainer>
                            <AreaChart data={visitorsStats.daily.map(d => ({ ...d, date: new Date(d.date).toLocaleDateString("fr-FR", { day: "2-digit", month: "short" }) }))}>
                              <CartesianGrid strokeDasharray="3 3" stroke={C.border} vertical={false} />
                              <XAxis dataKey="date" tick={{ fontSize: 10, fill: C.inkMuted }} stroke={C.border} interval={Math.max(0, Math.floor(visitorsStats.daily.length / 8))} />
                              <YAxis tick={{ fontSize: 10, fill: C.inkMuted }} stroke={C.border} allowDecimals={false} />
                              <Tooltip />
                              <Area type="monotone" dataKey="visitors" stroke={CHART[0]} strokeWidth={2} fill={C.navySoft} name="Visiteurs" />
                              <Area type="monotone" dataKey="views" stroke={CHART[2]} strokeWidth={1.5} fill="transparent" strokeDasharray="4 3" name="Vues" />
                            </AreaChart>
                          </ResponsiveContainer>
                        </div>
                      </div>
                    )}

                    {/* Device + Sources */}
                    <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))", gap: 12 }}>
                      {visitorsStats.byDevice && visitorsStats.byDevice.length > 0 && (
                        <div style={{ border: `1px solid ${C.border}`, borderRadius: R.lg, padding: 12 }}>
                          <div style={{ display: "flex", alignItems: "center", gap: 7, fontWeight: F.weight.semi, fontSize: F.size.base, color: C.ink, marginBottom: 10 }}>
                            <Icon name="mobile" size={15} color={C.inkMuted} />Appareils
                          </div>
                          {visitorsStats.byDevice.map((d, i) => {
                            const total = visitorsStats.byDevice.reduce((s, x) => s + x.visitors, 0);
                            const pctVal = total > 0 ? Math.round((d.visitors / total) * 100) : 0;
                            const iconName = d.device === "mobile" ? "mobile" : d.device === "tablet" ? "mobile" : "dashboard";
                            const label = d.device === "mobile" ? "Mobile" : d.device === "tablet" ? "Tablette" : "Ordinateur";
                            return (
                              <div key={i} style={{ marginBottom: 8 }}>
                                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", fontSize: F.size.sm, marginBottom: 4 }}>
                                  <span className="icon-text" style={{ fontWeight: F.weight.medium, color: C.inkBody }}>
                                    <Icon name={iconName} size={13} color={C.inkMuted} />{label}
                                  </span>
                                  <span style={{ fontWeight: F.weight.semi, color: C.ink, ...numeric }}>{d.visitors} ({pctVal} %)</span>
                                </div>
                                <div style={{ height: 5, background: C.surfaceAlt, border: `1px solid ${C.border}`, borderRadius: R.pill, overflow: "hidden" }}>
                                  <div style={{ height: "100%", width: `${pctVal}%`, background: C.navy }} />
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      )}

                      {visitorsStats.bySource && visitorsStats.bySource.length > 0 && (
                        <div style={{ border: `1px solid ${C.border}`, borderRadius: R.lg, padding: 12 }}>
                          <div style={{ display: "flex", alignItems: "center", gap: 7, fontWeight: F.weight.semi, fontSize: F.size.base, color: C.ink, marginBottom: 10 }}>
                            <Icon name="link" size={15} color={C.inkMuted} />Sources
                          </div>
                          {visitorsStats.bySource.slice(0, 6).map((d, i) => {
                            const total = visitorsStats.bySource.reduce((s, x) => s + x.visitors, 0);
                            const pctVal = total > 0 ? Math.round((d.visitors / total) * 100) : 0;
                            const iconName = { direct: "link", whatsapp: "chat", google: "search", facebook: "globe", instagram: "camera", twitter: "globe", tiktok: "globe", internal: "home" }[d.source] || "globe";
                            const label = d.source.charAt(0).toUpperCase() + d.source.slice(1);
                            return (
                              <div key={i} style={{ marginBottom: 8 }}>
                                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", fontSize: F.size.sm, marginBottom: 4 }}>
                                  <span className="icon-text" style={{ fontWeight: F.weight.medium, color: C.inkBody }}>
                                    <Icon name={iconName} size={13} color={C.inkMuted} />{label}
                                  </span>
                                  <span style={{ fontWeight: F.weight.semi, color: C.ink, ...numeric }}>{d.visitors} ({pctVal} %)</span>
                                </div>
                                <div style={{ height: 5, background: C.surfaceAlt, border: `1px solid ${C.border}`, borderRadius: R.pill, overflow: "hidden" }}>
                                  <div style={{ height: "100%", width: `${pctVal}%`, background: C.amber }} />
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      )}
                    </div>
                  </Card>
                )}

                {/* ═══ EVOLUTION MOIS ═══ */}
                {s.prevMonth && (
                  <Card style={{ marginBottom: 16 }}>
                    <SectionHeader icon="chart" title="Comparaison vs mois dernier" />
                    <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(150px, 1fr))", gap: 12 }}>
                      {[
                        ["CA", s.month?.revenue || 0, s.prevMonth?.revenue || 0, "FCFA"],
                        ["Commandes", s.month?.orders || 0, s.prevMonth?.orders || 0, ""],
                        ["Nvx clients", s.newClientsMonth || 0, s.newClientsPrev || 0, ""],
                        ["Panier moyen", s.month?.orders ? Math.round((s.month.revenue||0) / s.month.orders) : 0, s.prevMonth?.orders ? Math.round((s.prevMonth.revenue||0) / s.prevMonth.orders) : 0, "F"],
                      ].map(([label, cur, prev, unit], i) => {
                        const diff = pct(cur, prev);
                        const up = Number(diff) >= 0;
                        return (
                          <div key={i} style={{ border: `1px solid ${C.border}`, borderRadius: R.md, padding: "11px 13px", minWidth: 0 }}>
                            <div style={{ fontSize: F.size.sm, color: C.inkMuted, fontWeight: F.weight.medium, marginBottom: 5 }}>{label}</div>
                            <div style={{ fontSize: F.size.xl, fontWeight: F.weight.bold, color: C.ink, letterSpacing: "-.02em", ...numeric }}>
                              {typeof cur === "number" ? cur.toLocaleString() : cur}{unit ? ` ${unit}` : ""}
                            </div>
                            <div style={{ display: "flex", alignItems: "center", gap: 4, fontSize: F.size.sm, fontWeight: F.weight.medium, color: up ? C.success : C.danger, marginTop: 4 }}>
                              <Icon name={up ? "trendUp" : "trendDown"} size={12} />
                              <span style={numeric}>{Math.abs(Number(diff))} %</span>
                            </div>
                            <div style={{ fontSize: F.size.xs, color: C.inkFaint, marginTop: 2, ...numeric }}>
                              vs {typeof prev === "number" ? prev.toLocaleString() : prev}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </Card>
                )}

                {/* ═══ ALERTES ═══ */}
                {s.unpaid && s.unpaid.count > 0 && (
                  <Callout tone="danger" icon="alert" title="Impayés" style={{ marginBottom: 16 }}>
                    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 12, flexWrap: "wrap" }}>
                      <span>{s.unpaid.count} commande(s) non soldée(s)</span>
                      <strong style={{ fontSize: F.size.lg, color: C.danger, ...numeric }}>{s.unpaid.amount.toLocaleString()} F</strong>
                    </div>
                  </Callout>
                )}

                {/* ═══ CA — AREA CHART avec toggle Jour/Semaine/Mois ═══ */}
                {((revenuePeriod === "daily" && s.dailyRevenue?.length) || (revenuePeriod === "weekly" && s.weeklyRevenue?.length) || (revenuePeriod === "monthly" && s.monthlyRevenue?.length)) && (
                  <Card style={{ marginBottom: 16 }}>
                    <SectionHeader
                      icon="money"
                      title="Chiffre d'affaires"
                      actions={
                        <div style={{ display: "flex", gap: 2, background: C.surfaceAlt, border: `1px solid ${C.border}`, borderRadius: R.md, padding: 2 }}>
                          {[["daily","Jour"],["weekly","Semaine"],["monthly","Mois"]].map(([v,l]) => (
                            <button key={v} type="button" onClick={() => setRevenuePeriod(v)} style={{
                              padding: "5px 12px", borderRadius: R.sm, border: "none", cursor: "pointer",
                              fontSize: F.size.sm, fontWeight: F.weight.semi,
                              background: revenuePeriod === v ? C.surface : "transparent",
                              color: revenuePeriod === v ? C.navy : C.inkMuted,
                              boxShadow: revenuePeriod === v ? SH.sm : "none",
                            }}>{l}</button>
                          ))}
                        </div>
                      }
                    />
                    <div style={{ width: "100%", height: 260 }}>
                      <ResponsiveContainer>
                        <AreaChart data={(revenuePeriod === "daily" ? s.dailyRevenue : revenuePeriod === "weekly" ? s.weeklyRevenue : s.monthlyRevenue).map(d => ({
                          ...d,
                          date: new Date(d.date).toLocaleDateString("fr-FR", revenuePeriod === "monthly" ? { month: "short", year: "2-digit" } : { day: "2-digit", month: "short" }),
                        }))}>
                          <CartesianGrid strokeDasharray="3 3" stroke={C.border} vertical={false} />
                          <XAxis dataKey="date" tick={{ fontSize: 10, fill: C.inkMuted }} stroke={C.border} />
                          <YAxis tick={{ fontSize: 10, fill: C.inkMuted }} stroke={C.border} tickFormatter={fk} />
                          <Tooltip formatter={(v) => [`${Number(v).toLocaleString()} FCFA`, "CA"]} labelStyle={{ fontWeight: F.weight.semi }} />
                          <Area type="monotone" dataKey="revenue" stroke={CHART[0]} strokeWidth={2} fill={C.navySoft} />
                        </AreaChart>
                      </ResponsiveContainer>
                    </div>
                  </Card>
                )}

                {/* ═══ COMMANDES PAR JOUR ═══ */}
                {s.dailyRevenue && s.dailyRevenue.length > 0 && (
                  <Card style={{ marginBottom: 16 }}>
                    <SectionHeader icon="package" title="Commandes par jour" />
                    <div style={{ width: "100%", height: 220 }}>
                      <ResponsiveContainer>
                        <BarChart data={s.dailyRevenue.map(d => ({ ...d, date: new Date(d.date).toLocaleDateString("fr-FR", { day: "2-digit", month: "short" }) }))}>
                          <CartesianGrid strokeDasharray="3 3" stroke={C.border} vertical={false} />
                          <XAxis dataKey="date" tick={{ fontSize: 10, fill: C.inkMuted }} stroke={C.border} interval={Math.max(0, Math.floor(s.dailyRevenue.length / 8))} />
                          <YAxis tick={{ fontSize: 10, fill: C.inkMuted }} stroke={C.border} allowDecimals={false} />
                          <Tooltip />
                          <Bar dataKey="orders" fill={CHART[3]} radius={[R.sm, R.sm, 0, 0]} name="Commandes" />
                        </BarChart>
                      </ResponsiveContainer>
                    </div>
                  </Card>
                )}

                {/* ═══ ROW: Statuts + Paiements ═══ */}
                <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(280px, 1fr))", gap: 12, marginBottom: 16 }}>
                  {/* Par statut */}
                  {s.byStatus && (
                    <Card>
                      <SectionHeader icon="chart" title="Par statut" />
                      <div style={{ width: "100%", height: 180 }}>
                        <ResponsiveContainer>
                          <PieChart>
                            <Pie data={Object.entries(s.byStatus).map(([name, value]) => ({ name, value }))}
                              cx="50%" cy="50%" innerRadius={35} outerRadius={65} dataKey="value" label={({ value }) => value}>
                              {Object.keys(s.byStatus).map((st, i) => (
                                <Cell key={i} fill={STATUS_COLORS[st] || CHART[i % CHART.length]} />
                              ))}
                            </Pie>
                            <Tooltip />
                          </PieChart>
                        </ResponsiveContainer>
                      </div>
                      <div style={{ display: "flex", flexWrap: "wrap", gap: 8, marginTop: 8, alignItems: "center" }}>
                        {Object.entries(s.byStatus).map(([st, c]) => (
                          <span key={st} style={{ display: "inline-flex", alignItems: "center", gap: 4 }}>
                            <Badge status={st} small />
                            <span style={{ fontSize: F.size.sm, color: C.inkMuted, ...numeric }}>{c}</span>
                          </span>
                        ))}
                      </div>
                    </Card>
                  )}

                  {/* Répartition paiements */}
                  {s.byPayment && s.byPayment.length > 0 && (
                    <Card>
                      <SectionHeader icon="card" title="Paiements" />
                      <div style={{ width: "100%", height: 180 }}>
                        <ResponsiveContainer>
                          <PieChart>
                            <Pie data={s.byPayment.map(p => ({ name: PAY_LABELS[p.status] || p.status, value: p.count }))}
                              cx="50%" cy="50%" innerRadius={35} outerRadius={65} dataKey="value" label={({ value }) => value}>
                              {s.byPayment.map((p, i) => (
                                <Cell key={i} fill={PAY_COLORS[p.status] || C.inkFaint} />
                              ))}
                            </Pie>
                            <Tooltip />
                          </PieChart>
                        </ResponsiveContainer>
                      </div>
                      <div style={{ display: "flex", flexWrap: "wrap", gap: 5, marginTop: 8 }}>
                        {s.byPayment.map(p => (
                          <Chip key={p.status} small tone={p.status === "paye" ? "success" : p.status === "non_paye" ? "danger" : p.status === "avance" ? "warning" : "neutral"}>
                            {PAY_LABELS[p.status] || p.status} · <span style={numeric}>{p.count}</span>
                          </Chip>
                        ))}
                      </div>
                    </Card>
                  )}
                </div>

                {/* ═══ ROW: Collectes vs Sur place + Méthodes de paiement ═══ */}
                <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(280px, 1fr))", gap: 12, marginBottom: 16 }}>
                  {/* Collectes vs Sur place */}
                  {s.byType && s.byType.length > 0 && (
                    <Card>
                      <SectionHeader icon="collect" title="Collectes / sur place" />
                      <div style={{ width: "100%", height: 180 }}>
                        <ResponsiveContainer>
                          <PieChart>
                            <Pie data={s.byType.map(t => ({ name: TYPE_LABELS[t.type] || t.type, value: t.count }))}
                              cx="50%" cy="50%" innerRadius={35} outerRadius={65} dataKey="value" label={({ name, value }) => `${name}: ${value}`}>
                              {s.byType.map((t, i) => (
                                <Cell key={i} fill={TYPE_COLORS[t.type] || C.inkFaint} />
                              ))}
                            </Pie>
                            <Tooltip />
                          </PieChart>
                        </ResponsiveContainer>
                      </div>
                      {s.byType.map(t => (
                        <div key={t.type} style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "5px 0", fontSize: F.size.base, borderTop: `1px solid ${C.border}` }}>
                          <span className="icon-text" style={{ color: C.inkBody, fontWeight: F.weight.medium }}>
                            <Icon name={t.type === "delivery" ? "delivery" : "store"} size={13} color={C.inkMuted} />
                            {TYPE_LABELS[t.type] || t.type}
                          </span>
                          <span style={{ color: C.ink, fontWeight: F.weight.semi, ...numeric }}>{t.revenue.toLocaleString()} F</span>
                        </div>
                      ))}
                    </Card>
                  )}

                  {/* CA par méthode de paiement */}
                  {s.byPayMethod && s.byPayMethod.length > 0 && (
                    <Card>
                      <SectionHeader icon="cash" title="CA par méthode" />
                      <div style={{ width: "100%", height: 180 }}>
                        <ResponsiveContainer>
                          <BarChart data={s.byPayMethod.map(m => ({ name: METHOD_LABELS[m.method] || m.method, revenue: m.revenue, count: m.count }))} layout="vertical">
                            <CartesianGrid strokeDasharray="3 3" stroke={C.border} horizontal={false} />
                            <XAxis type="number" tick={{ fontSize: 10, fill: C.inkMuted }} stroke={C.border} tickFormatter={fk} />
                            <YAxis type="category" dataKey="name" tick={{ fontSize: 11, fill: C.inkBody }} stroke={C.border} width={80} />
                            <Tooltip formatter={(v) => [`${Number(v).toLocaleString()} FCFA`, "CA"]} />
                            <Bar dataKey="revenue" radius={[0, R.sm, R.sm, 0]}>
                              {s.byPayMethod.map((m, i) => (
                                <Cell key={i} fill={METHOD_COLORS[m.method] || CHART[0]} />
                              ))}
                            </Bar>
                          </BarChart>
                        </ResponsiveContainer>
                      </div>
                    </Card>
                  )}
                </div>

                {/* ═══ HEURES DE POINTE ═══ */}
                {s.byHour && s.byHour.length > 0 && (
                  <Card style={{ marginBottom: 16 }}>
                    <SectionHeader icon="clock" title="Heures de pointe" />
                    <div style={{ width: "100%", height: 200 }}>
                      <ResponsiveContainer>
                        <BarChart data={Array.from({ length: 24 }, (_, h) => ({ hour: `${h}h`, count: (s.byHour.find(x => x.hour === h) || {}).count || 0 }))}>
                          <CartesianGrid strokeDasharray="3 3" stroke={C.border} vertical={false} />
                          <XAxis dataKey="hour" tick={{ fontSize: 9, fill: C.inkMuted }} stroke={C.border} interval={1} />
                          <YAxis tick={{ fontSize: 10, fill: C.inkMuted }} stroke={C.border} allowDecimals={false} />
                          <Tooltip formatter={(v) => [v, "Commandes"]} />
                          <Bar dataKey="count" radius={[R.sm, R.sm, 0, 0]}>
                            {Array.from({ length: 24 }, (_, h) => {
                              const c = (s.byHour.find(x => x.hour === h) || {}).count || 0;
                              const maxC = Math.max(...s.byHour.map(x => x.count));
                              const intensity = maxC > 0 ? c / maxC : 0;
                              return <Cell key={h} fill={intensity > 0.7 ? C.navy : intensity > 0.4 ? CHART[1] : intensity > 0 ? "#94a3b8" : C.border} />;
                            })}
                          </Bar>
                        </BarChart>
                      </ResponsiveContainer>
                    </div>
                  </Card>
                )}

                {/* ═══ ROW: Top Articles + Top Collecteurs ═══ */}
                <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(280px, 1fr))", gap: 12, marginBottom: 16 }}>
                  {/* Top articles — Bar chart */}
                  {s.topArticles && s.topArticles.length > 0 && (
                    <Card>
                      <SectionHeader icon="garment" title="Top articles" subtitle="Par quantité" />
                      <div style={{ width: "100%", height: Math.max(200, s.topArticles.slice(0, 8).length * 28) }}>
                        <ResponsiveContainer>
                          <BarChart data={s.topArticles.slice(0, 8)} layout="vertical" margin={{ left: 10, right: 20 }}>
                            <CartesianGrid strokeDasharray="3 3" stroke={C.border} horizontal={false} />
                            <XAxis type="number" tick={{ fontSize: 10, fill: C.inkMuted }} stroke={C.border} allowDecimals={false} />
                            <YAxis type="category" dataKey="name" tick={{ fontSize: 11, fill: C.inkBody }} stroke={C.border} width={90} />
                            <Tooltip formatter={(v, n) => n === "qty" ? [`${v} pcs`, "Quantité"] : [`${Number(v).toLocaleString()} F`, "CA"]} labelStyle={{ fontWeight: F.weight.semi }} />
                            <Bar dataKey="qty" fill={CHART[0]} radius={[0, R.sm, R.sm, 0]} name="qty" />
                          </BarChart>
                        </ResponsiveContainer>
                      </div>
                    </Card>
                  )}

                  {/* Top collecteurs */}
                  {s.byCollector && s.byCollector.length > 0 && (
                    <Card>
                      <SectionHeader icon="medal" title="Top collecteurs" />
                      <div className="tbl-wrap">
                        <table className="tbl">
                          <thead>
                            <tr><th style={{ width: 34 }}>#</th><th>Collecteur</th><th className="num">Collectes</th></tr>
                          </thead>
                          <tbody>
                            {s.byCollector.slice(0, 5).map((c, i) => (
                              <tr key={i}>
                                <td style={{ color: C.inkFaint, ...numeric }}>{i + 1}</td>
                                <td style={{ fontWeight: F.weight.medium, color: C.ink }}>{c.name}</td>
                                <td className="num" style={{ fontWeight: F.weight.semi, color: C.ink }}>{c.count}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </Card>
                  )}
                </div>

                {/* ═══ CA PAR ETABLISSEMENT ═══ */}
                {s.byEtab && s.byEtab.length > 0 && (
                  <Card style={{ marginBottom: 16 }}>
                    <SectionHeader icon="building" title="CA par établissement" />
                    {s.byEtab.map((e, i) => {
                      const maxRev = Math.max(...s.byEtab.map(x => x.revenue));
                      return (
                        <div key={i} style={{ marginBottom: 10 }}>
                          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", gap: 10, fontSize: F.size.base, marginBottom: 5 }}>
                            <span style={{ fontWeight: F.weight.medium, color: C.inkBody }}>{e.name}</span>
                            <div style={{ flexShrink: 0 }}>
                              <span style={{ fontWeight: F.weight.semi, color: C.ink, ...numeric }}>{e.revenue.toLocaleString()} F</span>
                              <span style={{ fontSize: F.size.sm, color: C.inkFaint, marginLeft: 6, ...numeric }}>{e.count} cmd</span>
                            </div>
                          </div>
                          <div style={{ height: 6, background: C.surfaceAlt, border: `1px solid ${C.border}`, borderRadius: R.pill, overflow: "hidden" }}>
                            <div style={{ height: "100%", width: `${maxRev > 0 ? (e.revenue / maxRev * 100) : 0}%`, background: C.navy }} />
                          </div>
                        </div>
                      );
                    })}
                  </Card>
                )}

                {/* ═══ TOP CLIENTS ═══ */}
                {s.topClients && s.topClients.length > 0 && (
                  <Card style={{ marginBottom: 16 }}>
                    <SectionHeader icon="users" title="Meilleurs clients" />
                    <div className="tbl-wrap">
                      <table className="tbl">
                        <thead>
                          <tr><th>Client</th><th>Téléphone</th><th className="num">Commandes</th><th className="num">Dépensé</th></tr>
                        </thead>
                        <tbody>
                          {s.topClients.slice(0, 8).map((c, i) => (
                            <tr key={i}>
                              <td style={{ fontWeight: F.weight.medium, color: C.ink }}>{c.client}</td>
                              <td style={{ color: C.inkMuted, ...numeric }}>{c.phone}</td>
                              <td className="num">{c.orders}</td>
                              <td className="num" style={{ fontWeight: F.weight.semi, color: C.ink }}>{c.spent.toLocaleString()} F</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </Card>
                )}

                {/* ═══ RESUME GLOBAL ═══ */}
                <Card style={{ marginBottom: 16 }}>
                  <SectionHeader icon="trendUp" title="Résumé global" />
                  <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(150px, 1fr))", gap: 10 }}>
                    {[
                      ["CA total", `${(s.total?.revenue || 0).toLocaleString()} F`],
                      ["Total commandes", s.total?.orders || 0],
                      ["Panier moyen", `${s.total?.orders ? Math.round(s.total.revenue / s.total.orders).toLocaleString() : 0} F`],
                      ["Clients uniques", s.totalClients || 0],
                      ["CA semaine", `${(s.week?.revenue || 0).toLocaleString()} F`],
                      ["Taux payé", `${s.total?.orders ? Math.round(((s.byPayment || []).find(p => p.status === "paye") || {}).count / s.total.orders * 100) : 0} %`],
                    ].map(([label, value], i) => (
                      <div key={i} style={{ border: `1px solid ${C.border}`, borderRadius: R.md, padding: "10px 12px", minWidth: 0 }}>
                        <div style={{ fontSize: F.size.sm, color: C.inkMuted, fontWeight: F.weight.medium }}>{label}</div>
                        <div style={{ fontSize: F.size.lg, fontWeight: F.weight.semi, color: C.ink, marginTop: 3, ...numeric }}>{value}</div>
                      </div>
                    ))}
                  </div>
                </Card>

                <Btn fullWidth variant="ghost" icon="refresh" onClick={() => { setAdminStatsLoading(true); api.getStats().then(d => setAdminStats(d)).catch(() => {}).finally(() => setAdminStatsLoading(false)); }}>
                  Rafraîchir les données
                </Btn>
              </div>
            ) : (
              <Card padded={false}>
                <EmptyState
                  icon="chart"
                  title="Statistiques indisponibles"
                  description="Impossible de charger les statistiques pour le moment."
                  action={<Btn small icon="refresh" onClick={() => { setAdminStatsLoading(true); api.getStats().then(d => setAdminStats(d)).catch(() => {}).finally(() => setAdminStatsLoading(false)); }}>Réessayer</Btn>}
                />
              </Card>
            )}
          </div>
        );
        })()}

        {/* Etablissements tab */}
        {tab === "etabs" && (
          <div style={{ marginTop: 8 }}>
            <SectionHeader
              icon="building"
              title="Établissements"
              subtitle="Pressings et laveries rattachés au compte"
              actions={<Btn small icon="add" onClick={() => openEtabForm()}>Nouvel établissement</Btn>}
            />
            {etabLoading ? <Spinner /> : etabs.length === 0 ? (
              <Card padded={false}>
                <EmptyState
                  icon="building"
                  title="Aucun établissement"
                  description="Créez votre premier pressing ou votre première laverie."
                  action={<Btn small icon="add" onClick={() => openEtabForm()}>Créer un établissement</Btn>}
                />
              </Card>
            ) : (
              <div className="tbl-wrap">
                <table className="tbl">
                  <thead>
                    <tr>
                      <th>Établissement</th><th>Type</th><th>Adresse</th><th>Contact</th>
                      <th>Statut</th><th style={{ width: 80 }}>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {etabs.map(e => (
                      <tr key={e.id} style={{ opacity: e.active ? 1 : 0.55 }}>
                        <td>
                          <span className="icon-text" style={{ fontWeight: F.weight.semi, color: C.ink }}>
                            <Icon name={e.type === "laverie" ? "washing" : "garment"} size={14} color={C.inkMuted} />
                            {e.name}
                          </span>
                        </td>
                        <td><Chip small tone={e.type === "laverie" ? "info" : "navy"}>{e.type === "laverie" ? "Laverie" : "Pressing"}</Chip></td>
                        <td style={{ color: C.inkMuted }}>{e.address || "—"}</td>
                        <td style={{ color: C.inkMuted, ...numeric }}>
                          {e.phone && <div>{e.phone}</div>}
                          {e.email && <div style={{ fontVariantNumeric: "normal" }}>{e.email}</div>}
                          {!e.phone && !e.email && "—"}
                        </td>
                        <td><Chip small tone={e.active ? "success" : "danger"}>{e.active ? "Actif" : "Inactif"}</Chip></td>
                        <td>
                          <div style={{ display: "flex", gap: 2 }}>
                            <IconBtn icon="edit" title="Modifier" onClick={() => openEtabForm(e)} />
                            <IconBtn
                              icon={e.active ? "forbidden" : "check"}
                              title={e.active ? "Désactiver" : "Activer"}
                              danger={e.active}
                              onClick={() => toggleEtabActive(e)}
                            />
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
            {showEtabForm && (
              <Modal
                title={editEtab ? "Modifier l'établissement" : "Nouvel établissement"}
                icon="building"
                onClose={() => setShowEtabForm(false)}
                width={500}
              >
                <form onSubmit={submitEtab}>
                  <Input label="Nom" required value={etabName} onChange={setEtabName} placeholder="ex : Setal Express Plateau" />
                  <div style={{ marginBottom: 14 }}>
                    <label style={{ display: "block", fontWeight: F.weight.medium, marginBottom: 5, fontSize: F.size.base, color: C.inkBody }}>
                      Type <span style={{ color: C.danger }} aria-hidden="true">*</span>
                    </label>
                    <div style={{ display: "flex", gap: 8 }}>
                      {[["pressing", "Pressing", "garment"], ["laverie", "Laverie", "washing"]].map(([v, l, ic]) => (
                        <label key={v} style={{
                          flex: 1, padding: "10px 12px", borderRadius: R.md, cursor: "pointer",
                          border: `1px solid ${etabType === v ? C.navy : C.border}`,
                          background: etabType === v ? C.navySoft : C.surface,
                          color: etabType === v ? C.navy : C.inkBody,
                          fontWeight: F.weight.medium, fontSize: F.size.md,
                          display: "flex", alignItems: "center", justifyContent: "center", gap: 7,
                        }}>
                          <input type="radio" name="etabType" value={v} checked={etabType === v} onChange={() => setEtabType(v)} style={{ display: "none" }} />
                          <Icon name={ic} size={15} />{l}
                        </label>
                      ))}
                    </div>
                  </div>
                  <Input label="Adresse" value={etabAddress} onChange={setEtabAddress} icon="location" placeholder="ex : Dakar, Plateau" />
                  <Input label="Téléphone" value={etabPhone} onChange={setEtabPhone} icon="phone" placeholder="ex : 77 000 00 00" />
                  <Input label="Email" value={etabEmail} onChange={setEtabEmail} type="email" icon="mail" placeholder="ex : contact@setal.sn" />
                  <div style={{ display: "flex", gap: 8, justifyContent: "flex-end" }}>
                    <Btn variant="ghost" onClick={() => setShowEtabForm(false)}>Annuler</Btn>
                    <Btn type="submit" icon="save" loading={etabSaving}>Enregistrer</Btn>
                  </div>
                </form>
              </Modal>
            )}
          </div>
        )}

        {/* Comptabilite tab */}
        {tab === "comptabilite" && (
          <div style={{ marginTop: 8 }}>
            <SectionHeader icon="chart" title="Comptabilité" />
            <Comptabilite user={user} etablissements={etabs} reloadEtabs={loadEtabs} />
          </div>
        )}

        {/* Categories tab */}
        {tab === "categories" && (
          <div style={{ marginTop: 8 }}>
            <SectionHeader
              icon="layers"
              title="Catégories de dépenses"
              subtitle="Utilisées dans le module de comptabilité"
              actions={<Btn small icon="add" onClick={() => openExpCatForm()}>Nouvelle catégorie</Btn>}
            />
            {expCatLoading ? <Spinner /> : expCats.length === 0 ? (
              <Card padded={false}>
                <EmptyState
                  icon="layers"
                  title="Aucune catégorie"
                  description="Créez une catégorie pour classer vos dépenses."
                  action={<Btn small icon="add" onClick={() => openExpCatForm()}>Nouvelle catégorie</Btn>}
                />
              </Card>
            ) : (
              <div className="tbl-wrap">
                <table className="tbl">
                  <thead>
                    <tr><th style={{ width: 44 }}>Icône</th><th>Catégorie</th><th style={{ width: 80 }}>Actions</th></tr>
                  </thead>
                  <tbody>
                    {expCats.map(c => (
                      <tr key={c.id}>
                        <td><span style={{ color: C.inkMuted }}><DataIcon value={c.icon} fallback="package" size={16} /></span></td>
                        <td style={{ fontWeight: F.weight.medium, color: C.ink }}>{c.name}</td>
                        <td>
                          <div style={{ display: "flex", gap: 2 }}>
                            <IconBtn icon="edit" title="Modifier" onClick={() => openExpCatForm(c)} />
                            <IconBtn icon="delete" title="Supprimer" danger onClick={() => deleteExpCat(c)} />
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
            {showExpCatForm && (
              <Modal
                title={editExpCat ? "Modifier la catégorie" : "Nouvelle catégorie"}
                icon="layers"
                onClose={() => setShowExpCatForm(false)}
              >
                <form onSubmit={submitExpCat}>
                  <IconPicker label="Icône" value={ecIcon} onChange={setEcIcon} />
                  <Input label="Nom" required value={ecName} onChange={setEcName} placeholder="ex : Salaires" />
                  <div style={{ display: "flex", gap: 8, justifyContent: "flex-end" }}>
                    <Btn variant="ghost" onClick={() => setShowExpCatForm(false)}>Annuler</Btn>
                    <Btn type="submit" icon="save" loading={ecSaving}>Enregistrer</Btn>
                  </div>
                </form>
              </Modal>
            )}
          </div>
        )}

        {/* Articles tab */}
        {tab === "articles" && (
          <div style={{ marginTop: 8 }}>
            <SectionHeader
              icon="list"
              title="Articles & prix"
              subtitle="Repris dans le formulaire collecteur et l'estimateur du site"
              actions={<Btn small icon="add" onClick={() => openArtForm()}>Nouvel article</Btn>}
            />
            <Callout tone="info" icon="idea" style={{ marginBottom: 16 }}>
              Les articles définis ici apparaissent dans le formulaire collecteur et dans l'estimateur de prix du site.
            </Callout>
            {artLoading ? <Spinner /> : artList.length === 0 ? (
              <Card padded={false}>
                <EmptyState
                  icon="list"
                  title="Aucun article configuré"
                  description="Ajoutez un premier article pour alimenter le catalogue."
                  action={<Btn small icon="add" onClick={() => openArtForm()}>Nouvel article</Btn>}
                />
              </Card>
            ) : (
              <div className="tbl-wrap">
                <table className="tbl">
                  <thead>
                    <tr>
                      <th style={{ width: 44 }}>Icône</th>
                      <th>Article</th>
                      <th>Intitulé site</th>
                      <th className="num">Prix (FCFA)</th>
                      <th>Statut</th>
                      <th>Site</th>
                      <th style={{ width: 108 }}>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {artList.map(a => (
                      <tr key={a.id} style={{ opacity: a.active ? 1 : 0.55 }}>
                        <td><span style={{ color: C.inkMuted }}><DataIcon value={a.icon} size={16} /></span></td>
                        <td style={{ fontWeight: F.weight.medium, color: C.ink }}>{a.name}</td>
                        <td style={{ color: C.inkMuted }}>{a.display_name || "—"}</td>
                        <td className="num" style={{ fontWeight: F.weight.semi, color: C.ink }}>
                          {parseFloat(a.price).toLocaleString("fr-FR")}
                        </td>
                        <td><Chip small tone={a.active ? "success" : "danger"}>{a.active ? "Actif" : "Masqué"}</Chip></td>
                        <td>
                          <button
                            type="button"
                            title={a.show_on_landing ? "Retirer de la page d'accueil" : "Afficher sur la page d'accueil"}
                            onClick={async () => {
                              await api.adminUpdateArticle(a.id, { show_on_landing: !a.show_on_landing }); loadArticles();
                            }}
                            style={{ padding: 0, background: "none", border: "none" }}
                          >
                            <Chip small tone={a.show_on_landing ? "success" : "neutral"} icon={a.show_on_landing ? "show" : "hide"}>
                              {a.show_on_landing ? "Visible" : "Masqué"}
                            </Chip>
                          </button>
                        </td>
                        <td>
                          <div style={{ display: "flex", gap: 2 }}>
                            <IconBtn icon="edit" title="Modifier" onClick={() => openArtForm(a)} />
                            <IconBtn
                              icon={a.active ? "forbidden" : "check"}
                              title={a.active ? "Désactiver" : "Activer"}
                              danger={a.active}
                              onClick={async () => {
                                await api.adminUpdateArticle(a.id, { active: !a.active }); loadArticles();
                              }}
                            />
                            <IconBtn icon="delete" title="Supprimer" danger onClick={() => deleteArt(a)} />
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
            {showArtForm && (
              <Modal
                title={editArt ? "Modifier l'article" : "Nouvel article"}
                icon="list"
                onClose={() => setShowArtForm(false)}
              >
                <form onSubmit={submitArt}>
                  <IconPicker value={artIcon} onChange={setArtIcon} />
                  <Input label="Nom de l'article" required value={artName} onChange={setArtName} placeholder="ex : Chemise" />
                  <Input label="Intitulé pour le site" value={artDisplayName} onChange={setArtDisplayName} placeholder="ex : Chemise repassée" />
                  <Input label="Prix unitaire (FCFA)" required value={artPrice} onChange={setArtPrice} type="number" placeholder="ex : 1000" />
                  <div style={{ display: "flex", flexDirection: "column", gap: 10, marginBottom: 16 }}>
                    <Toggle checked={artActive} onChange={setArtActive} label="Article actif" />
                    <Toggle checked={artShowLanding} onChange={setArtShowLanding} label="Afficher sur la page d'accueil" />
                  </div>
                  <div style={{ display: "flex", gap: 8, justifyContent: "flex-end" }}>
                    <Btn variant="ghost" onClick={() => setShowArtForm(false)}>Annuler</Btn>
                    <Btn type="submit" icon="save" loading={artSaving}>Enregistrer</Btn>
                  </div>
                </form>
              </Modal>
            )}
          </div>
        )}

        {/* Horaires tab */}
        {tab === "horaires" && (
          <div style={{ marginTop: 8 }}>
            <SectionHeader
              icon="clock"
              title="Horaires de collecte"
              subtitle="Créneaux proposés aux clients à la commande"
              actions={<Btn small icon="add" onClick={() => openSlotForm()}>Nouveau créneau</Btn>}
            />
            <Callout tone="info" icon="idea" style={{ marginBottom: 16 }}>
              Ces créneaux définissent quand vous passez chercher le linge chez le client.
              Un créneau actif est proposé sans limite de nombre de collectes.
            </Callout>
            {slotsLoading ? <Spinner /> : Object.keys(slotsByDay).length === 0 ? (
              <Card padded={false}>
                <EmptyState
                  icon="clock"
                  title="Aucun créneau configuré"
                  description="Ajoutez des créneaux pour que les clients puissent planifier une collecte."
                  action={<Btn small icon="add" onClick={() => openSlotForm()}>Créer un créneau</Btn>}
                />
              </Card>
            ) : (
              <div style={{ display: "grid", gap: 12 }}>
                {[1,2,3,4,5,6,0].map(day => {
                  const daySlots = slotsByDay[day];
                  if (!daySlots || daySlots.length === 0) return null;
                  return (
                    <Card key={day} padded={false}>
                      <div style={{
                        display: "flex", alignItems: "center", gap: 8,
                        padding: "10px 14px", borderBottom: `1px solid ${C.border}`, background: C.surfaceAlt,
                      }}>
                        <Icon name="calendar" size={15} color={C.inkMuted} />
                        <h3 style={{ fontSize: F.size.md, fontWeight: F.weight.semi, color: C.ink }}>{DAYS_FR[day]}</h3>
                        <Chip small>{daySlots.length} créneau{daySlots.length > 1 ? "x" : ""}</Chip>
                      </div>
                      <div className="tbl-wrap" style={{ border: "none", borderRadius: 0 }}>
                        <table className="tbl">
                          <thead>
                            <tr><th>Créneau</th><th>Capacité</th><th>Statut</th><th style={{ width: 108 }}>Actions</th></tr>
                          </thead>
                          <tbody>
                            {daySlots.map(s => (
                              <tr key={s.id} style={{ opacity: s.active ? 1 : 0.55 }}>
                                <td>
                                  <span className="mono" style={{ ...monoRef, color: C.ink, fontWeight: F.weight.medium }}>
                                    {s.start_time} — {s.end_time}
                                  </span>
                                </td>
                                <td><Chip small tone={s.active ? "success" : "danger"}>{s.active ? "Actif" : "Désactivé"}</Chip></td>
                                <td>
                                  <div style={{ display: "flex", gap: 2 }}>
                                    <IconBtn icon="edit" title="Modifier" onClick={() => openSlotForm(s)} />
                                    <IconBtn icon={s.active ? "forbidden" : "check"} title={s.active ? "Désactiver" : "Activer"} danger={s.active} onClick={() => toggleSlotActive(s)} />
                                    <IconBtn icon="delete" title="Supprimer" danger onClick={() => deleteSlot(s)} />
                                  </div>
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </Card>
                  );
                })}
              </div>
            )}
            {showSlotForm && (
              <Modal
                title={editSlot ? "Modifier le créneau" : "Nouveau créneau de collecte"}
                icon="clock"
                onClose={() => setShowSlotForm(false)}
              >
                <form onSubmit={submitSlot}>
                  <Select label="Jour" required value={slotDay} onChange={v => setSlotDay(parseInt(v))}>
                    {DAYS_FR.map((d, i) => <option key={i} value={i}>{d}</option>)}
                  </Select>
                  <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
                    <Input label="Heure de début" required value={slotStart} onChange={setSlotStart} type="time" />
                    <Input label="Heure de fin" required value={slotEnd} onChange={setSlotEnd} type="time" />
                  </div>
                  <div style={{ display: "flex", gap: 8, justifyContent: "flex-end" }}>
                    <Btn variant="ghost" onClick={() => setShowSlotForm(false)}>Annuler</Btn>
                    <Btn type="submit" icon="save" loading={slotSaving}>Enregistrer</Btn>
                  </div>
                </form>
              </Modal>
            )}
          </div>
        )}

        {/* Livraison tab */}
        {tab === "livraison" && (
          <div style={{ marginTop: 8 }}>
            {/* ── Frais de livraison config ── */}
            <Card style={{ marginBottom: 20 }}>
              <SectionHeader
                icon="collect"
                title="Frais de collecte et de livraison"
                actions={<Toggle checked={!!deliveryFeesConfig.enabled} onChange={() => setDeliveryFeesConfig(c => ({ ...c, enabled: !c.enabled }))} label={deliveryFeesConfig.enabled ? "Activé" : "Désactivé"} />}
              />
              {deliveryFeesConfig.enabled && (
                <>
                  <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))", gap: 12 }}>
                    <Input
                      label="Campus (FCFA)" icon="graduate" type="number"
                      value={deliveryFeesConfig.campus_fee}
                      onChange={v => setDeliveryFeesConfig(c => ({ ...c, campus_fee: Number(v) }))}
                    />
                    <Input
                      label="Hors campus (FCFA)" icon="home" type="number"
                      value={deliveryFeesConfig.hors_campus_fee}
                      onChange={v => setDeliveryFeesConfig(c => ({ ...c, hors_campus_fee: Number(v) }))}
                    />
                    <Input
                      label="Gratuit à partir de (FCFA)" icon="gift" type="number"
                      value={deliveryFeesConfig.free_threshold}
                      onChange={v => setDeliveryFeesConfig(c => ({ ...c, free_threshold: Number(v) }))}
                    />
                  </div>
                  <Callout tone="neutral" icon="idea" style={{ marginBottom: 12 }}>
                    Le collecteur choisit la zone lors de la collecte. Si le total articles atteint{" "}
                    <strong style={numeric}>{(deliveryFeesConfig.free_threshold || 5000).toLocaleString()} F</strong>, la livraison est offerte.
                  </Callout>
                </>
              )}
              <Btn small icon="save" loading={dfSaving} onClick={async () => {
                setDfSaving(true);
                try {
                  await api.saveDeliveryFeesConfig(deliveryFeesConfig);
                  alert("Configuration enregistrée.");
                } catch (err) { alert("Erreur : " + err.message); }
                finally { setDfSaving(false); }
              }}>Enregistrer</Btn>
            </Card>

            {/* ── Créneaux horaires ── */}
            <SectionHeader
              icon="delivery"
              title="Horaires de livraison"
              subtitle="Proposés au client dès que la commande passe en « Prêt »"
              actions={<Btn small icon="add" onClick={() => openDSlotForm()}>Nouveau créneau</Btn>}
            />
            <Callout tone="info" icon="idea" style={{ marginBottom: 16 }}>
              Ces créneaux définissent quand vous rapportez le linge propre au client.
              Un créneau actif est proposé sans limite de nombre de livraisons.
            </Callout>
            {dSlotsLoading ? <Spinner /> : Object.keys(dSlotsByDay).length === 0 ? (
              <Card padded={false}>
                <EmptyState
                  icon="delivery"
                  title="Aucun créneau de livraison"
                  description="Ajoutez des créneaux pour que les clients puissent planifier leur livraison."
                  action={<Btn small icon="add" onClick={() => openDSlotForm()}>Créer un créneau</Btn>}
                />
              </Card>
            ) : (
              <div style={{ display: "grid", gap: 12 }}>
                {[1,2,3,4,5,6,0].map(day => {
                  const daySlots = dSlotsByDay[day];
                  if (!daySlots || daySlots.length === 0) return null;
                  return (
                    <Card key={day} padded={false}>
                      <div style={{
                        display: "flex", alignItems: "center", gap: 8,
                        padding: "10px 14px", borderBottom: `1px solid ${C.border}`, background: C.surfaceAlt,
                      }}>
                        <Icon name="delivery" size={15} color={C.inkMuted} />
                        <h3 style={{ fontSize: F.size.md, fontWeight: F.weight.semi, color: C.ink }}>{DAYS_FR[day]}</h3>
                        <Chip small>{daySlots.length} créneau{daySlots.length > 1 ? "x" : ""}</Chip>
                      </div>
                      <div className="tbl-wrap" style={{ border: "none", borderRadius: 0 }}>
                        <table className="tbl">
                          <thead>
                            <tr><th>Créneau</th><th>Capacité</th><th>Statut</th><th style={{ width: 108 }}>Actions</th></tr>
                          </thead>
                          <tbody>
                            {daySlots.map(s => (
                              <tr key={s.id} style={{ opacity: s.active ? 1 : 0.55 }}>
                                <td>
                                  <span className="mono" style={{ ...monoRef, color: C.ink, fontWeight: F.weight.medium }}>
                                    {s.start_time} — {s.end_time}
                                  </span>
                                </td>
                                <td><Chip small tone={s.active ? "success" : "danger"}>{s.active ? "Actif" : "Désactivé"}</Chip></td>
                                <td>
                                  <div style={{ display: "flex", gap: 2 }}>
                                    <IconBtn icon="edit" title="Modifier" onClick={() => openDSlotForm(s)} />
                                    <IconBtn icon={s.active ? "forbidden" : "check"} title={s.active ? "Désactiver" : "Activer"} danger={s.active} onClick={() => toggleDSlotActive(s)} />
                                    <IconBtn icon="delete" title="Supprimer" danger onClick={() => deleteDSlot(s)} />
                                  </div>
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </Card>
                  );
                })}
              </div>
            )}
            {showDSlotForm && (
              <Modal
                title={editDSlot ? "Modifier le créneau" : "Nouveau créneau de livraison"}
                icon="delivery"
                onClose={() => setShowDSlotForm(false)}
              >
                <form onSubmit={submitDSlot}>
                  <Select label="Jour" required value={dSlotDay} onChange={v => setDSlotDay(parseInt(v))}>
                    {DAYS_FR.map((d, i) => <option key={i} value={i}>{d}</option>)}
                  </Select>
                  <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
                    <Input label="Heure de début" required value={dSlotStart} onChange={setDSlotStart} type="time" />
                    <Input label="Heure de fin" required value={dSlotEnd} onChange={setDSlotEnd} type="time" />
                  </div>
                  <div style={{ display: "flex", gap: 8, justifyContent: "flex-end" }}>
                    <Btn variant="ghost" onClick={() => setShowDSlotForm(false)}>Annuler</Btn>
                    <Btn type="submit" icon="save" loading={dSlotSaving}>Enregistrer</Btn>
                  </div>
                </form>
              </Modal>
            )}
          </div>
        )}

        {/* Users tab */}
        {tab === "users" && (
          <div style={{ marginTop: 8 }}>
            <SectionHeader
              icon="users"
              title="Utilisateurs"
              subtitle="Comptes administrateurs, gestionnaires et collecteurs"
              actions={<Btn small icon="userAdd" onClick={() => openUserForm()}>Nouvel utilisateur</Btn>}
            />
            {uError && <Callout tone="danger" icon="alert" style={{ marginBottom: 12 }}>{uError}</Callout>}
            {uLoading ? <Spinner /> : users.length === 0 ? (
              <Card padded={false}>
                <EmptyState
                  icon="users"
                  title="Aucun utilisateur"
                  description="Créez un compte pour votre équipe."
                  action={<Btn small icon="userAdd" onClick={() => openUserForm()}>Nouvel utilisateur</Btn>}
                />
              </Card>
            ) : (
              <div className="tbl-wrap">
                <table className="tbl">
                  <thead>
                    <tr>
                      <th>Utilisateur</th><th>Identifiant</th><th>Rôle</th>
                      <th>Établissements</th><th>Privilèges</th><th>Statut</th><th style={{ width: 108 }}>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {users.map(u => (
                      <tr key={u.id} style={{ opacity: u.active ? 1 : 0.55 }}>
                        <td style={{ fontWeight: F.weight.medium, color: C.ink }}>{u.name}</td>
                        <td><Ref>@{u.login}</Ref></td>
                        <td>
                          <Chip small tone={u.role === "admin" ? "danger" : u.role === "manager" ? "info" : "success"}>
                            {ROLE_LABELS[u.role] || u.role}
                          </Chip>
                        </td>
                        <td>
                          {u.establishments && u.establishments.length > 0 ? (
                            <div style={{ display: "flex", gap: 4, flexWrap: "wrap" }}>
                              {u.establishments.map(e => <Chip key={e.id} small icon="building">{e.name}</Chip>)}
                            </div>
                          ) : <span style={{ color: C.inkFaint }}>—</span>}
                        </td>
                        <td>
                          {u.role === "manager" && u.permissions ? (
                            <Chip small tone="neutral" icon="key">
                              {MANAGER_PERMISSIONS.filter(p => u.permissions[p.key]).length}/{MANAGER_PERMISSIONS.length}
                            </Chip>
                          ) : u.role === "admin" ? (
                            <Chip small tone="navy" icon="shield">Tous</Chip>
                          ) : <span style={{ color: C.inkFaint }}>—</span>}
                        </td>
                        <td><Chip small tone={u.active ? "success" : "danger"}>{u.active ? "Actif" : "Inactif"}</Chip></td>
                        <td>
                          <div style={{ display: "flex", gap: 2 }}>
                            <IconBtn icon="edit" title="Modifier" onClick={() => openUserForm(u)} />
                            <IconBtn icon={u.active ? "forbidden" : "check"} title={u.active ? "Désactiver" : "Activer"} danger={u.active} onClick={() => toggleUserActive(u)} />
                            {String(u.id) !== String(user?.id) && (
                              <IconBtn icon="delete" title="Supprimer" danger onClick={() => deleteUser(u)} />
                            )}
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
            {showUForm && (
              <Modal
                title={editUser ? "Modifier l'utilisateur" : "Nouvel utilisateur"}
                icon={editUser ? "edit" : "userAdd"}
                onClose={() => setShowUForm(false)}
                width={500}
              >
                <form onSubmit={submitUser}>
                  {!editUser && <Input label="Identifiant" value={uLogin} onChange={setULogin} placeholder="ex : agent1" required />}
                  <Input label="Nom complet" value={uName} onChange={setUName} placeholder="ex : Mamadou Fall" required />
                  <Input label={editUser ? "Nouveau mot de passe" : "Mot de passe"} value={uPass} onChange={setUPass} type="password" placeholder="••••••••" required={!editUser} />
                  <Select label="Rôle" required value={uRole} onChange={v => setURole(v)}>
                    <option value="collector">Collecteur</option>
                    <option value="manager">Gestionnaire</option>
                    <option value="admin">Administrateur</option>
                  </Select>
                  {(uRole === "manager" || uRole === "collector") && etabs.length > 0 && (
                    <div style={{ marginBottom: 14 }}>
                      <label style={{ display: "flex", alignItems: "center", gap: 5, fontWeight: F.weight.medium, marginBottom: 5, fontSize: F.size.base, color: C.inkBody }}>
                        Établissements
                        <span style={{ color: C.inkFaint, fontWeight: F.weight.normal }}>· {uRole === "manager" ? "obligatoire" : "optionnel"}</span>
                      </label>
                      <div style={{ border: `1px solid ${C.border}`, borderRadius: R.md, background: C.surfaceAlt, padding: 6, maxHeight: 160, overflowY: "auto" }}>
                        {etabs.filter(e => e.active).map(e => (
                          <label key={e.id} style={{
                            display: "flex", alignItems: "center", gap: 8, padding: "6px 8px", cursor: "pointer",
                            borderRadius: R.sm, background: uEtabs.includes(e.id) ? C.navySoft : "transparent",
                          }}>
                            <input type="checkbox" checked={uEtabs.includes(e.id)}
                              onChange={ev => {
                                if (ev.target.checked) setUEtabs(prev => [...prev, e.id]);
                                else setUEtabs(prev => prev.filter(id => id !== e.id));
                              }}
                              style={{ width: 15, height: 15, accentColor: C.navy }} />
                            <span className="icon-text" style={{ fontSize: F.size.base, fontWeight: uEtabs.includes(e.id) ? F.weight.semi : F.weight.normal, color: uEtabs.includes(e.id) ? C.navy : C.inkBody }}>
                              <Icon name="building" size={13} color={C.inkMuted} />
                              {e.name}
                            </span>
                          </label>
                        ))}
                      </div>
                      <p style={{ fontSize: F.size.sm, color: C.inkFaint, marginTop: 5 }}>
                        {uRole === "manager" ? "Le gestionnaire ne verra que les données de ces établissements." : "Le collecteur pourra déposer les vêtements dans ces établissements."}
                      </p>
                    </div>
                  )}
                  {/* Permissions for managers */}
                  {uRole === "manager" && (
                    <div style={{ marginBottom: 14 }}>
                      <label style={{ display: "block", fontWeight: F.weight.medium, marginBottom: 6, fontSize: F.size.base, color: C.inkBody }}>
                        Privilèges
                      </label>
                      {/* Presets */}
                      <div style={{ display: "flex", gap: 6, marginBottom: 10, flexWrap: "wrap" }}>
                        {Object.entries(PERMISSION_PRESETS).map(([k, preset]) => {
                          const selected = JSON.stringify(uPerms) === JSON.stringify(preset.perms);
                          return (
                            <button key={k} type="button" onClick={() => setUPerms({ ...preset.perms })} style={{
                              padding: "5px 11px", borderRadius: R.md, fontSize: F.size.sm, fontWeight: F.weight.semi, cursor: "pointer",
                              border: `1px solid ${selected ? C.navy : C.border}`,
                              background: selected ? C.navySoft : C.surface,
                              color: selected ? C.navy : C.inkBody,
                            }}>{preset.label}</button>
                          );
                        })}
                      </div>
                      {/* Individual toggles */}
                      <div style={{ border: `1px solid ${C.border}`, borderRadius: R.md, overflow: "hidden" }}>
                        {MANAGER_PERMISSIONS.map((p, i) => (
                          <div key={p.key} style={{
                            display: "flex", alignItems: "center", justifyContent: "space-between", gap: 10,
                            padding: "8px 12px",
                            borderBottom: i < MANAGER_PERMISSIONS.length - 1 ? `1px solid ${C.border}` : "none",
                            background: uPerms[p.key] ? C.surfaceAlt : C.surface,
                          }}>
                            {p.alwaysOn ? (
                              <>
                                <span style={{ fontSize: F.size.base, color: C.inkBody }}>{p.label}</span>
                                <Chip small tone="success">Toujours actif</Chip>
                              </>
                            ) : (
                              <Toggle
                                checked={!!uPerms[p.key]}
                                onChange={v => setUPerms(prev => ({ ...prev, [p.key]: v }))}
                                label={p.label}
                              />
                            )}
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                  <div style={{ display: "flex", gap: 8, justifyContent: "flex-end" }}>
                    <Btn variant="ghost" onClick={() => setShowUForm(false)}>Annuler</Btn>
                    <Btn type="submit" icon="save" loading={uSaving}>Enregistrer</Btn>
                  </div>
                </form>
              </Modal>
            )}
          </div>
        )}

        {/* Services tab */}
        {tab === "services" && (
          <div style={{ marginTop: 8 }}>
            <SectionHeader
              icon="washing"
              title="Services"
              subtitle="Prestations mises en avant sur le site"
              actions={<Btn small icon="add" onClick={() => openSvcForm()}>Nouveau service</Btn>}
            />
            {sError && <Callout tone="danger" icon="alert" style={{ marginBottom: 12 }}>{sError}</Callout>}
            {sLoading ? <Spinner /> : services.length === 0 ? (
              <Card padded={false}>
                <EmptyState
                  icon="washing"
                  title="Aucun service"
                  description="Créez un premier service pour l'afficher sur le site."
                  action={<Btn small icon="add" onClick={() => openSvcForm()}>Nouveau service</Btn>}
                />
              </Card>
            ) : (
              <div className="tbl-wrap">
                <table className="tbl">
                  <thead>
                    <tr>
                      <th style={{ width: 44 }}>Icône</th>
                      <th>Service</th>
                      <th>Description</th>
                      <th>Prix affiché</th>
                      <th>Statut</th>
                      <th style={{ width: 108 }}>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {services.map(s => (
                      <tr key={s.id} style={{ opacity: s.active ? 1 : 0.55 }}>
                        <td><span style={{ color: C.inkMuted }}><DataIcon value={s.icon} size={16} /></span></td>
                        <td style={{ fontWeight: F.weight.medium, color: C.ink }}>{s.label}</td>
                        <td style={{ color: C.inkMuted }}>{s.description || "—"}</td>
                        <td style={{ color: C.inkMuted, ...numeric }}>{s.price_display || "—"}</td>
                        <td><Chip small tone={s.active ? "success" : "danger"}>{s.active ? "Visible" : "Masqué"}</Chip></td>
                        <td>
                          <div style={{ display: "flex", gap: 2 }}>
                            <IconBtn icon="edit" title="Modifier" onClick={() => openSvcForm(s)} />
                            <IconBtn
                              icon={s.active ? "hide" : "show"}
                              title={s.active ? "Masquer" : "Afficher"}
                              danger={s.active}
                              onClick={async () => { await api.adminUpdateService(s.id, { active: !s.active }); loadServices(); }}
                            />
                            <IconBtn icon="delete" title="Supprimer" danger onClick={() => deleteSvc(s)} />
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
            {showSForm && (
              <Modal
                title={editSvc ? "Modifier le service" : "Nouveau service"}
                icon="washing"
                onClose={() => setShowSForm(false)}
              >
                <form onSubmit={submitSvc}>
                  <IconPicker value={sIcon} onChange={setSIcon} />
                  <Input label="Nom" value={sLabel} onChange={setSLabel} placeholder="ex : Chemises" required />
                  <Textarea label="Description" value={sDesc} onChange={setSDesc} />
                  <Input label="Prix affiché sur le site" value={sPriceDisplay} onChange={setSPriceDisplay} placeholder="ex : À partir de 500 FCFA" />
                  <div style={{ display: "flex", gap: 8, justifyContent: "flex-end" }}>
                    <Btn variant="ghost" onClick={() => setShowSForm(false)}>Annuler</Btn>
                    <Btn type="submit" icon="save" loading={sSaving}>Enregistrer</Btn>
                  </div>
                </form>
              </Modal>
            )}
          </div>
        )}

        {/* Pricing tab */}
        {tab === "pricing" && (
          <div style={{ marginTop: 8 }}>
            <SectionHeader
              icon="money"
              title="Tarifs"
              subtitle="Formules affichées sur la page tarifs du site"
              actions={<Btn small icon="add" onClick={() => openPrcForm()}>Nouveau tarif</Btn>}
            />
            {pError && <Callout tone="danger" icon="alert" style={{ marginBottom: 12 }}>{pError}</Callout>}
            {pLoading ? <Spinner /> : pricing.length === 0 ? (
              <Card padded={false}>
                <EmptyState
                  icon="money"
                  title="Aucun tarif"
                  description="Créez une première formule tarifaire."
                  action={<Btn small icon="add" onClick={() => openPrcForm()}>Nouveau tarif</Btn>}
                />
              </Card>
            ) : (
              <div className="tbl-wrap">
                <table className="tbl">
                  <thead>
                    <tr>
                      <th>Formule</th>
                      <th>Description</th>
                      <th className="num">Prix</th>
                      <th>Mise en avant</th>
                      <th>Statut</th>
                      <th style={{ width: 108 }}>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {pricing.map(p => (
                      <tr key={p.id} style={{ opacity: p.active ? 1 : 0.55 }}>
                        <td style={{ fontWeight: F.weight.medium, color: C.ink }}>{p.title}</td>
                        <td style={{ color: C.inkMuted }}>{p.description || "—"}</td>
                        <td className="num" style={{ fontWeight: F.weight.semi, color: C.ink }}>{p.price}</td>
                        <td>
                          {p.popular
                            ? <Chip small tone="warning" icon="star">Populaire</Chip>
                            : <span style={{ color: C.inkFaint }}>—</span>}
                        </td>
                        <td><Chip small tone={p.active ? "success" : "danger"}>{p.active ? "Visible" : "Masqué"}</Chip></td>
                        <td>
                          <div style={{ display: "flex", gap: 2 }}>
                            <IconBtn icon="edit" title="Modifier" onClick={() => openPrcForm(p)} />
                            <IconBtn
                              icon={p.active ? "hide" : "show"}
                              title={p.active ? "Masquer" : "Afficher"}
                              danger={p.active}
                              onClick={async () => { await api.adminUpdatePricing(p.id, { active: !p.active }); loadPricing(); }}
                            />
                            <IconBtn icon="delete" title="Supprimer" danger onClick={() => deletePrc(p)} />
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
            {showPForm && (
              <Modal
                title={editPrc ? "Modifier le tarif" : "Nouveau tarif"}
                icon="money"
                onClose={() => setShowPForm(false)}
              >
                <form onSubmit={submitPrc}>
                  <Input label="Titre" value={pTitle} onChange={setPTitle} required />
                  <Input label="Prix" value={pPrice} onChange={setPPrice} required placeholder="ex : 5 000 FCFA" />
                  <Textarea label="Description" value={pDesc} onChange={setPDesc} />
                  <div style={{ display: "flex", flexDirection: "column", gap: 10, marginBottom: 16 }}>
                    <Toggle checked={pPopular} onChange={setPPopular} label="Mettre en avant (formule populaire)" />
                    <Toggle checked={pActive} onChange={setPActive} label="Visible sur le site" />
                  </div>
                  <div style={{ display: "flex", gap: 8, justifyContent: "flex-end" }}>
                    <Btn variant="ghost" onClick={() => setShowPForm(false)}>Annuler</Btn>
                    <Btn type="submit" icon="save" loading={pSaving}>Enregistrer</Btn>
                  </div>
                </form>
              </Modal>
            )}
          </div>
        )}

        {/* MFA tab */}
        {tab === "mfa" && <MFASetup user={user} />}

        {/* Logs tab */}
        {tab === "logs" && (
          <div style={{ marginTop: 8 }}>
            <SectionHeader icon="document" title="Journal d'audit" subtitle="Actions réalisées sur le compte" />
            {logsLoading ? <Spinner /> : logs.length === 0 ? (
              <Card padded={false}>
                <EmptyState
                  icon="document"
                  title="Aucune entrée"
                  description="Aucun événement n'a encore été enregistré pour cette page."
                />
              </Card>
            ) : (
              <>
                <div className="tbl-wrap">
                  <table className="tbl">
                    <thead>
                      <tr>
                        <th>Date</th><th>Utilisateur</th><th>Action</th><th>Détails</th>
                      </tr>
                    </thead>
                    <tbody>
                      {logs.map((log, i) => (
                        <tr key={i}>
                          <td style={{ color: C.inkMuted, whiteSpace: "nowrap", ...numeric }}>{new Date(log.created_at).toLocaleString("fr-FR")}</td>
                          <td style={{ fontWeight: F.weight.medium, color: C.ink }}>{log.user_name || "—"}</td>
                          <td><Chip small tone="navy">{log.action}</Chip></td>
                          <td className="truncate" style={{ color: C.inkMuted, maxWidth: 220 }}>
                            {log.details ? JSON.stringify(log.details).substring(0, 60) : "—"}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                <div style={{ display: "flex", gap: 8, marginTop: 12, alignItems: "center", justifyContent: "center" }}>
                  <Btn small variant="ghost" icon="back" disabled={logsPage <= 1} onClick={() => setLogsPage(p => p - 1)}>Précédent</Btn>
                  <span style={{ padding: "0 10px", fontSize: F.size.base, color: C.inkMuted, ...numeric }}>Page {logsPage}</span>
                  <Btn small variant="ghost" iconRight="forward" onClick={() => setLogsPage(p => p + 1)}>Suivant</Btn>
                </div>
              </>
            )}
          </div>
        )}

        {/* Notifications tab */}
        {tab === "notifs" && (
          <div style={{ marginTop: 8 }}>
            {/* ═══ NOTIFICATION MESSAGES CONFIG ═══ */}
            <SectionHeader
              icon="edit"
              title="Personnaliser les messages"
              subtitle="Texte des notifications envoyées aux clients"
            />
            <Callout tone="neutral" icon="idea" style={{ marginBottom: 12 }}>
              <div style={{ marginBottom: 6 }}>Variables disponibles :</div>
              <div style={{ display: "flex", flexWrap: "wrap", gap: 5 }}>
                {["{orderId}", "{clientName}", "{trackLink}", "{deliveryLink}", "{reviewLink}", "{collectorName}"].map(v => (
                  <code key={v} className="mono" style={{ background: C.surface, border: `1px solid ${C.border}`, borderRadius: R.sm, padding: "1px 6px", fontSize: F.size.sm, color: C.ink }}>{v}</code>
                ))}
              </div>
            </Callout>

            <Callout tone="warning" icon="alert" title="Les SMS sont facturés à l'envoi" style={{ marginBottom: 16 }}>
              Un SMS tient en <strong>160 caractères</strong>, liens compris. Au-delà, il est
              découpé et facturé en plusieurs envois.
              <br />
              Les caractères <strong>ê î ô û ç</strong> et les emojis n'existent pas dans
              l'alphabet SMS : un seul suffit à faire tomber la limite à <strong>70 caractères</strong>.
              Le serveur les convertit automatiquement (« prêt » devient « pret »), mais mieux vaut
              les éviter à la rédaction. Les accents <strong>é è à ù</strong> passent sans problème.
            </Callout>

            {[
              { icon: "mobile", title: "SMS de suivi (MTarget)", items: [
                { key: "sms_collecte_confirmee", label: "1 · Demande de collecte confirmée" },
                { key: "sms_pret_livraison", label: "2 · Linge prêt — choix du créneau" },
                { key: "sms_pret_surplace", label: "2 · Linge prêt — retrait en boutique" },
                { key: "sms_rappel_livraison", label: "3 · Relance unique (24 h)" },
                { key: "sms_merci_avis", label: "4 · Remerciement et avis" },
              ]},
              { icon: "bell", title: "Notifications push — titres", items: [
                { key: "push_receptionne_title", label: "Réceptionné" },
                { key: "push_collecte_title", label: "Collecté" },
                { key: "push_traitement_title", label: "En traitement" },
                { key: "push_pret_title", label: "Prêt" },
                { key: "push_livre_title", label: "Livré" },
                { key: "push_recupere_title", label: "Récupéré" },
                { key: "push_collecteur_enroute_title", label: "Collecteur en route" },
                { key: "push_collecteur_arrive_title", label: "Collecteur arrivé" },
              ]},
              { icon: "mobile", title: "Notifications push — corps", items: [
                { key: "push_receptionne_body", label: "Réceptionné" },
                { key: "push_collecte_body", label: "Collecté" },
                { key: "push_traitement_body", label: "En traitement" },
                { key: "push_pret_body_livraison", label: "Prêt (livraison)" },
                { key: "push_pret_body_surplace", label: "Prêt (sur place)" },
                { key: "push_livre_body", label: "Livré" },
                { key: "push_recupere_body", label: "Récupéré" },
                { key: "push_collecteur_enroute_body", label: "Collecteur en route" },
                { key: "push_collecteur_arrive_body", label: "Collecteur arrivé" },
              ]},
              { icon: "clock", title: "Relance de livraison", items: [
                { key: "rappel_push_1", label: "Titre de la notification push" },
              ]},
            ].map(section => (
              <Card key={section.title} style={{ marginBottom: 14 }}>
                <h3 className="icon-text" style={{ fontWeight: F.weight.semi, color: C.ink, fontSize: F.size.lg, marginBottom: 12 }}>
                  <Icon name={section.icon} size={16} color={C.inkMuted} />
                  {section.title}
                </h3>
                <div style={{ display: "grid", gap: 10 }}>
                  {section.items.map(item => {
                    const defaults = {
                      sms_collecte_confirmee: "Setal Express : votre demande de collecte {orderId} est confirmée. Suivi : {trackLink}",
                      sms_pret_livraison: "Setal Express : commande {orderId} terminée. Choisissez votre créneau de livraison : {deliveryLink}",
                      sms_pret_surplace: "Setal Express : commande {orderId} terminée. Vous pouvez venir la retirer. Suivi : {trackLink}",
                      sms_rappel_livraison: "Setal Express : votre commande {orderId} vous attend. Choisissez votre créneau de livraison : {deliveryLink}",
                      sms_merci_avis: "Merci d'avoir choisi Setal Express ! Votre avis nous aide beaucoup : {reviewLink}",
                      push_receptionne_title: "📦 Linge réceptionné !", push_receptionne_body: "Votre linge a été réceptionné sur place. Commande {orderId} — nous nous en occupons !",
                      push_collecte_title: "📦 Linge collecté !", push_collecte_body: "Votre linge a été récupéré. Commande {orderId} — nous nous en occupons !",
                      push_traitement_title: "🧺 Linge en traitement", push_traitement_body: "Votre linge est en cours de lavage/repassage. Commande {orderId}.",
                      push_pret_title: "✨ Votre linge est prêt !", push_pret_body_livraison: "Commande {orderId} prête. Choisissez votre créneau de livraison.", push_pret_body_surplace: "Commande {orderId} prête. Vous pouvez venir le récupérer sur place.",
                      push_livre_title: "✅ Linge livré !", push_livre_body: "Commande {orderId} livrée avec succès. Merci de votre confiance !",
                      push_recupere_title: "✅ Linge récupéré !", push_recupere_body: "Commande {orderId} récupérée avec succès. Merci de votre confiance !",
                      push_collecteur_enroute_title: "🚗 Votre collecteur est en route !", push_collecteur_enroute_body: "{collectorName} se dirige vers vous pour la commande {orderId}. Suivez-le en temps réel !",
                      push_collecteur_arrive_title: "📍 Collecteur arrivé !", push_collecteur_arrive_body: "{collectorName} est arrivé à votre adresse pour la commande {orderId}. Préparez votre linge !",
                      rappel_push_1: "✨ Votre linge est prêt !",
                    };
                    const isLong = item.key.startsWith("sms_");
                    return (
                      <div key={item.key}>
                        <label style={{ fontSize: F.size.base, fontWeight: F.weight.medium, color: C.inkBody, marginBottom: 5, display: "block" }}>{item.label}</label>
                        {isLong ? (
                          <textarea
                            className="field"
                            rows={3}
                            value={notifMessages[item.key] ?? defaults[item.key] ?? ""}
                            onChange={e => setNotifMessages(prev => ({ ...prev, [item.key]: e.target.value }))}
                            style={{ resize: "vertical", fontFamily: F.mono, lineHeight: 1.5 }}
                          />
                        ) : (
                          <input
                            className="field"
                            type="text"
                            value={notifMessages[item.key] ?? defaults[item.key] ?? ""}
                            onChange={e => setNotifMessages(prev => ({ ...prev, [item.key]: e.target.value }))}
                          />
                        )}
                      </div>
                    );
                  })}
                </div>
              </Card>
            ))}

            <Btn fullWidth icon="save" loading={notifMsgSaving} style={{ marginBottom: 24 }}
              onClick={async () => {
                setNotifMsgSaving(true);
                try {
                  await api.saveSettings({ notification_messages: JSON.stringify(notifMessages) });
                  alert("Messages de notification enregistrés.");
                } catch (err) { alert("Erreur : " + err.message); }
                finally { setNotifMsgSaving(false); }
              }}>
              {notifMsgSaving ? "Sauvegarde…" : "Sauvegarder les messages"}
            </Btn>

            <SectionHeader icon="bell" title="Historique des notifications" />
            {notifsLoading ? <Spinner /> : notifs.length === 0 ? (
              <Card padded={false}>
                <EmptyState
                  icon="bell"
                  title="Aucune notification envoyée"
                  description="Les notifications envoyées aux clients apparaîtront ici."
                />
              </Card>
            ) : (
              <div style={{ display: "grid", gap: 10 }}>
                {notifs.map((n, i) => (
                  <Card key={i} style={{ padding: "12px 14px" }}>
                    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 10, marginBottom: 6 }}>
                      <div className="icon-text" style={{ minWidth: 0 }}>
                        <Icon name={n.channel === "whatsapp" ? "chat" : "mail"} size={15} color={C.inkMuted} />
                        <Ref>{n.order_id}</Ref>
                        <Icon name="forward" size={12} color={C.inkFaint} />
                        <span style={{ fontSize: F.size.sm, color: C.inkMuted, ...numeric }}>{n.phone}</span>
                      </div>
                      <Chip small tone={n.status === "sent" ? "success" : "danger"} icon={n.status === "sent" ? "check" : "close"}>
                        {n.status === "sent" ? "Envoyé" : "Échoué"}
                      </Chip>
                    </div>
                    <p style={{ fontSize: F.size.base, color: C.inkBody, marginBottom: 4, lineHeight: 1.5 }}>{n.message?.substring(0, 100)}</p>
                    <p style={{ fontSize: F.size.sm, color: C.inkFaint, ...numeric }}>{new Date(n.sent_at).toLocaleString("fr-FR")}</p>
                  </Card>
                ))}
              </div>
            )}
          </div>
        )}

        {/* WhatsApp Inbox */}
        {tab === "inbox" && (
          <div style={{ marginTop: 8 }}>
            <SectionHeader icon="inbox" title="Boîte de réception WhatsApp" subtitle="Conversations reçues des clients" />

            {waConvo ? (
              /* Conversation view */
              <div>
                <Btn variant="subtle" small icon="back" style={{ marginBottom: 10, paddingLeft: 0 }}
                  onClick={() => { setWaConvo(null); setWaReplyText(""); }}>
                  Retour à la boîte de réception
                </Btn>
                <Card style={{ marginBottom: 12 }}>
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 12 }}>
                    <div style={{ minWidth: 0 }}>
                      <p style={{ fontWeight: F.weight.semi, color: C.ink, fontSize: F.size.lg }}>{waConvo.client?.client || waConvo.phone}</p>
                      <p style={{ fontSize: F.size.base, color: C.inkMuted, ...numeric }}>+{waConvo.phone}</p>
                    </div>
                    <div style={{ display: "flex", gap: 4, flexShrink: 0 }}>
                      <IconBtn icon="phone" variant="outline" title="Appeler" onClick={() => window.location.href = `tel:+${waConvo.phone}`} />
                      <IconBtn icon="chat" variant="outline" title="Ouvrir dans WhatsApp" onClick={() => window.open(`https://wa.me/${waConvo.phone}`, "_blank")} />
                    </div>
                  </div>
                </Card>

                {/* Messages */}
                <div style={{ display: "flex", flexDirection: "column", gap: 6, marginBottom: 12, maxHeight: 400, overflowY: "auto", padding: "8px 0" }}>
                  {waConvoLoading ? <Spinner /> : (waConvo.messages || []).map(m => (
                    <div key={m.id} style={{
                      maxWidth: "80%", padding: "9px 12px", borderRadius: R.lg,
                      alignSelf: m.direction === "outgoing" ? "flex-end" : "flex-start",
                      background: m.direction === "outgoing" ? C.navy : C.surfaceAlt,
                      border: `1px solid ${m.direction === "outgoing" ? C.navy : C.border}`,
                      color: m.direction === "outgoing" ? C.inkOn : C.inkBody,
                      fontSize: F.size.md, lineHeight: 1.5,
                      borderBottomRightRadius: m.direction === "outgoing" ? R.sm : R.lg,
                      borderBottomLeftRadius: m.direction === "incoming" ? R.sm : R.lg,
                    }}>
                      <div style={{ whiteSpace: "pre-wrap", wordBreak: "break-word" }}>{m.body}</div>
                      <div style={{
                        display: "flex", alignItems: "center", gap: 4, marginTop: 4,
                        fontSize: F.size.xs, opacity: 0.7, ...numeric,
                        justifyContent: m.direction === "outgoing" ? "flex-end" : "flex-start",
                      }}>
                        {new Date(m.created_at).toLocaleString("fr-FR", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" })}
                        {m.direction === "outgoing" && m.status === "delivered" && <Icon name="checkDouble" size={12} />}
                        {m.direction === "outgoing" && m.status === "read" && <Icon name="checkDouble" size={12} />}
                        {m.direction === "outgoing" && m.status === "sent" && <Icon name="check" size={12} />}
                      </div>
                    </div>
                  ))}
                </div>

                {/* Reply input */}
                <div style={{ display: "flex", gap: 8 }}>
                  <input className="field" value={waReplyText} onChange={e => setWaReplyText(e.target.value)}
                    placeholder="Écrire un message…"
                    onKeyDown={e => {
                      if (e.key === "Enter" && waReplyText.trim() && !waReplySending) {
                        e.preventDefault();
                        (async () => {
                          setWaReplySending(true);
                          try {
                            await api.sendWaReply(waConvo.phone, waReplyText.trim());
                            setWaReplyText("");
                            const conv = await api.getWaConversation(waConvo.phone);
                            setWaConvo({ phone: waConvo.phone, ...conv });
                          } catch (err) { alert("Erreur : " + err.message); }
                          finally { setWaReplySending(false); }
                        })();
                      }
                    }}
                    style={{ flex: 1 }} />
                  <Btn icon="send" disabled={!waReplyText.trim() || waReplySending} loading={waReplySending} onClick={async () => {
                    setWaReplySending(true);
                    try {
                      await api.sendWaReply(waConvo.phone, waReplyText.trim());
                      setWaReplyText("");
                      const conv = await api.getWaConversation(waConvo.phone);
                      setWaConvo({ phone: waConvo.phone, ...conv });
                    } catch (err) { alert("Erreur : " + err.message); }
                    finally { setWaReplySending(false); }
                  }}>
                    Envoyer
                  </Btn>
                </div>
              </div>
            ) : (
              /* Inbox list */
              waInboxLoading ? <Spinner /> : waInbox.length === 0 ? (
                <Card padded={false}>
                  <EmptyState
                    icon="inbox"
                    title="Aucun message WhatsApp reçu"
                    description="Les messages des clients apparaîtront ici une fois le webhook configuré."
                  />
                </Card>
              ) : (
                <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
                  {waInbox.map(c => (
                    <Card key={c.phone} hover style={{ padding: "12px 14px", cursor: "pointer", borderColor: c.unread_count > 0 ? C.navy : C.border }}
                      onClick={async () => {
                        setWaConvoLoading(true);
                        setWaConvo({ phone: c.phone, messages: [], client: null });
                        try {
                          const conv = await api.getWaConversation(c.phone);
                          setWaConvo({ phone: c.phone, ...conv });
                        } catch {}
                        finally { setWaConvoLoading(false); }
                      }}>
                      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 12 }}>
                        <div style={{ flex: 1, minWidth: 0 }}>
                          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                            <span style={{ fontWeight: F.weight.semi, color: C.ink, fontSize: F.size.md }}>{c.client_name || `+${c.phone}`}</span>
                            {c.unread_count > 0 && (
                              <span style={{
                                minWidth: 18, height: 18, padding: "0 5px", borderRadius: R.pill,
                                background: C.navy, color: C.inkOn, fontSize: F.size.xs, fontWeight: F.weight.semi,
                                display: "inline-flex", alignItems: "center", justifyContent: "center", ...numeric,
                              }}>{c.unread_count}</span>
                            )}
                          </div>
                          <p className="truncate" style={{ fontSize: F.size.base, color: C.inkMuted, marginTop: 2, maxWidth: 280 }}>
                            {c.last_message || "—"}
                          </p>
                        </div>
                        <div style={{ textAlign: "right", flexShrink: 0, ...numeric }}>
                          <p style={{ fontSize: F.size.xs, color: C.inkFaint }}>{new Date(c.last_message_at).toLocaleString("fr-FR", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" })}</p>
                          <p style={{ fontSize: F.size.xs, color: C.inkFaint }}>{c.total_messages} msg</p>
                        </div>
                      </div>
                    </Card>
                  ))}
                </div>
              )
            )}
          </div>
        )}

        {/* Backup tab */}
        {tab === "sms" && (
          <div style={{ marginTop: 8, maxWidth: 620 }}>
            <SectionHeader
              icon="mobile"
              title="SMS de suivi"
              subtitle="Identifiants MTarget utilisés pour les messages envoyés aux clients"
            />

            <Card style={{ marginBottom: 14 }}>
              <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 14 }}>
                <Chip tone={mtUser && mtPassSet ? "success" : "warning"} icon={mtUser && mtPassSet ? "check" : "alert"}>
                  {mtUser && mtPassSet ? "Configuré" : "Non configuré"}
                </Chip>
                {!(mtUser && mtPassSet) && (
                  <span style={{ fontSize: F.size.base, color: C.inkMuted }}>
                    Aucun SMS ne part tant que les identifiants sont vides.
                  </span>
                )}
              </div>

              <Input
                label="Identifiant MTarget"
                icon="user"
                value={mtUser}
                onChange={setMtUser}
                placeholder="Fourni par MTarget"
              />
              <Input
                label="Mot de passe"
                icon="lock"
                type="password"
                value={mtPass}
                onChange={v => { setMtPass(v); setMtResult(null); }}
                hint={mtPassSet ? "déjà enregistré — laissez tel quel pour le conserver" : undefined}
                placeholder="Fourni par MTarget"
              />
              <Input
                label="Nom de l'expéditeur"
                icon="tag"
                value={mtSender}
                onChange={v => setMtSender(v.slice(0, 11))}
                maxLength={11}
                hint="11 caractères maximum, affiché à la place du numéro"
                placeholder="SetalExpr"
              />
              <Input
                label="Identifiant de service"
                icon="layers"
                value={mtServiceId}
                onChange={setMtServiceId}
                hint="fourni par MTarget — laissez vide si vous n'en avez pas"
                placeholder="ex : 36513"
              />
              <Input
                label="Adresse de l'API"
                icon="link"
                value={mtApiUrl}
                onChange={setMtApiUrl}
                hint="à changer uniquement si MTarget vous indique un autre hôte"
                placeholder="https://api-public.mtarget.fr/messages"
              />

              <Btn
                icon="save"
                loading={mtSaving}
                onClick={async () => {
                  setMtSaving(true); setMtResult(null);
                  try {
                    const payload = {
                      mtarget_username: mtUser,
                      mtarget_sender: mtSender,
                      mtarget_service_id: mtServiceId.trim(),
                      mtarget_api_url: mtApiUrl.trim(),
                    };
                    // Le masque signifie « ne pas toucher au mot de passe existant ».
                    if (mtPass && mtPass !== "••••••••") payload.mtarget_password = mtPass;
                    await api.saveSettings(payload);
                    if (payload.mtarget_password) { setMtPassSet(true); setMtPass("••••••••"); }
                    setMtResult({ ok: true, msg: "Identifiants enregistrés." });
                  } catch (e) {
                    setMtResult({ ok: false, msg: e.message });
                  } finally { setMtSaving(false); }
                }}
              >
                Enregistrer
              </Btn>
            </Card>

            <Card style={{ marginBottom: 14 }}>
              <h3 style={{ fontWeight: F.weight.semi, color: C.ink, fontSize: F.size.lg, marginBottom: 4 }}>
                Tester l'envoi
              </h3>
              <p style={{ fontSize: F.size.base, color: C.inkMuted, marginBottom: 14 }}>
                Un vrai SMS est envoyé et facturé. Utilisez votre propre numéro.
              </p>
              <Input
                label="Numéro de test"
                icon="mobile"
                value={mtTestPhone}
                onChange={setMtTestPhone}
                placeholder="77 123 45 67"
                inputMode="tel"
              />
              <Btn
                variant="ghost"
                icon="send"
                loading={mtTesting}
                disabled={!mtTestPhone.trim()}
                onClick={async () => {
                  setMtTesting(true); setMtResult(null);
                  try {
                    const r = await api.testSMS(mtTestPhone.trim());
                    setMtResult({
                      ok: true,
                      msg: `SMS envoyé à ${r.msisdn} — ${r.segments} SMS facturé${r.segments > 1 ? "s" : ""}.`,
                    });
                  } catch (e) {
                    setMtResult({ ok: false, msg: e.message });
                  } finally { setMtTesting(false); }
                }}
              >
                Envoyer un SMS de test
              </Btn>
            </Card>

            {mtResult && (
              <Callout tone={mtResult.ok ? "success" : "danger"} style={{ marginBottom: 14 }}>
                {mtResult.msg}
              </Callout>
            )}

            <Callout tone="neutral" icon="idea" title="Ce qui est envoyé">
              Quatre SMS au maximum sur toute la vie d'une commande : confirmation de la
              demande de collecte, linge terminé avec le choix du créneau, une relance
              unique après 24 h si aucun créneau n'est choisi, puis un remerciement avec
              demande d'avis. Les autres étapes ne déclenchent que des notifications push,
              qui sont gratuites. Le texte des messages se modifie dans l'onglet Notifications.
            </Callout>
          </div>
        )}

        {tab === "whatsapp" && (
          <div style={{ marginTop: 8 }}>
            <SectionHeader icon="chat" title="WhatsApp Business" subtitle="Connexion à l'API d'envoi de messages" />
            <Card style={{ marginBottom: 16 }}>
              <p style={{ color: C.inkMuted, fontSize: F.size.base, lineHeight: 1.6, marginBottom: 12 }}>
                Connectez votre compte WhatsApp Business API pour envoyer automatiquement les notifications de statut et les reçus PDF aux clients.
              </p>
              <Callout tone="info" icon="help" title="Comment obtenir ces informations" style={{ marginBottom: 16 }}>
                1. Allez sur <b>developers.facebook.com</b><br />
                2. Créez une App Business, puis ajoutez le produit WhatsApp<br />
                3. Dans WhatsApp, Configuration API : copiez le <b>Phone Number ID</b> et le <b>Token d'accès</b>
              </Callout>
              <form onSubmit={async (e) => {
                e.preventDefault(); setWaSaving(true);
                try {
                  await api.saveSettings({ wa_phone_id: waPhoneId, wa_token: waToken });
                  alert("Configuration WhatsApp enregistrée.");
                } catch (err) { alert("Erreur : " + err.message); }
                finally { setWaSaving(false); }
              }}>
                <div style={{ marginBottom: 14 }}>
                  <label style={{ display: "block", fontWeight: F.weight.medium, marginBottom: 5, fontSize: F.size.base, color: C.inkBody }}>Phone Number ID</label>
                  <input className="field" type="text" value={waPhoneId} onChange={e => setWaPhoneId(e.target.value)}
                    placeholder="ex : 123456789012345" />
                </div>
                <div style={{ marginBottom: 14 }}>
                  <label style={{ display: "block", fontWeight: F.weight.medium, marginBottom: 5, fontSize: F.size.base, color: C.inkBody }}>Token d'accès permanent</label>
                  <input className="field" type="password" value={waToken} onChange={e => setWaToken(e.target.value)}
                    placeholder="EAAxxxxxxx…" />
                </div>
                <Btn type="submit" icon="save" loading={waSaving} disabled={waSaving || !waPhoneId || !waToken}>
                  {waSaving ? "Sauvegarde…" : "Sauvegarder"}
                </Btn>
              </form>
            </Card>

            <Card>
              <SectionHeader icon="send" title="Tester la connexion" subtitle="Envoyez un message test pour vérifier la configuration" />
              <div style={{ display: "flex", gap: 8, alignItems: "flex-end", flexWrap: "wrap" }}>
                <div style={{ flex: 1, minWidth: 200 }}>
                  <label style={{ display: "block", fontWeight: F.weight.medium, marginBottom: 5, fontSize: F.size.base, color: C.inkBody }}>Numéro de test (avec indicatif)</label>
                  <input className="field" type="text" value={waTestPhone} onChange={e => setWaTestPhone(e.target.value)}
                    placeholder="221780000000" />
                </div>
                <Btn icon="send" loading={waTesting} disabled={waTesting || !waPhoneId || !waToken || !waTestPhone}
                  onClick={async () => {
                    setWaTesting(true); setWaTestResult(null);
                    try {
                      const r = await api.testWhatsApp(waPhoneId, waToken, waTestPhone);
                      setWaTestResult({ ok: true, msg: `Message envoyé ! ID: ${r.messageId}` });
                    } catch (err) {
                      setWaTestResult({ ok: false, msg: err.message });
                    } finally { setWaTesting(false); }
                  }}>
                  {waTesting ? "Envoi…" : "Envoyer un test"}
                </Btn>
              </div>
              {waTestResult && (
                <Callout tone={waTestResult.ok ? "success" : "danger"} style={{ marginTop: 12 }}>
                  {waTestResult.msg}
                </Callout>
              )}
            </Card>

            {/* ── Broadcast promo ── */}
            <Card style={{ marginTop: 16 }}>
              <SectionHeader
                icon="announce"
                title="Envoyer une promo aux clients"
                subtitle="Message promotionnel diffusé via WhatsApp"
              />

              {/* Message */}
              <div style={{ marginBottom: 16 }}>
                <label style={{ display: "block", fontWeight: F.weight.medium, marginBottom: 5, fontSize: F.size.base, color: C.inkBody }}>Message</label>
                <textarea className="field" value={broadcastMsg} onChange={e => setBroadcastMsg(e.target.value)}
                  placeholder={"Ex: 🎉 Offre spéciale Setal Express !\n\n-20% sur toutes vos commandes ce week-end avec le code PROMO20.\n\nRéservez maintenant sur setalexpress.com\n\n— Setal Express"}
                  rows={6} style={{ resize: "vertical", lineHeight: 1.5 }} />
                <p style={{ fontSize: F.size.sm, color: C.inkFaint, marginTop: 5, ...numeric }}>{broadcastMsg.length} caractères</p>
              </div>

              {/* Client selection */}
              <div style={{ marginBottom: 16 }}>
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 10, marginBottom: 8 }}>
                  <label style={{ fontWeight: F.weight.medium, fontSize: F.size.base, color: C.inkBody }}>
                    Destinataires <span style={{ color: C.inkFaint, ...numeric }}>({broadcastSelected.length}/{broadcastClients.length})</span>
                  </label>
                  <Btn small variant="ghost" icon={broadcastSelectAll ? "close" : "check"} onClick={() => {
                    if (broadcastSelectAll) { setBroadcastSelected([]); setBroadcastSelectAll(false); }
                    else { setBroadcastSelected(broadcastClients.map(c => c.phone)); setBroadcastSelectAll(true); }
                  }}>
                    {broadcastSelectAll ? "Tout désélectionner" : "Tout sélectionner"}
                  </Btn>
                </div>
                {broadcastLoading ? <Spinner label="Chargement des clients…" /> : (
                  <div style={{ maxHeight: 250, overflowY: "auto", border: `1px solid ${C.border}`, borderRadius: R.md, padding: 4, background: C.surfaceAlt }}>
                    {broadcastClients.length === 0 ? (
                      <EmptyState compact icon="users" title="Aucun client trouvé" />
                    ) : broadcastClients.map(c => {
                      const checked = broadcastSelected.includes(c.phone);
                      return (
                        <label key={c.phone} style={{
                          display: "flex", alignItems: "center", gap: 9, padding: "7px 10px", cursor: "pointer",
                          background: checked ? C.navySoft : "transparent", borderRadius: R.sm, marginBottom: 2,
                        }}>
                          <input type="checkbox" checked={checked} onChange={() => {
                            setBroadcastSelected(prev => checked ? prev.filter(p => p !== c.phone) : [...prev, c.phone]);
                          }} style={{ width: 15, height: 15, accentColor: C.navy }} />
                          <div style={{ flex: 1, minWidth: 0, display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
                            <span style={{ fontWeight: F.weight.medium, color: C.ink, fontSize: F.size.base }}>{c.name || "—"}</span>
                            <span style={{ color: C.inkMuted, fontSize: F.size.sm, ...numeric }}>{c.phone}</span>
                            {c.wa_optin === true && <Chip small tone="success" icon="check">WhatsApp</Chip>}
                            {c.wa_optin === false && <Chip small tone="danger" icon="close">WhatsApp</Chip>}
                          </div>
                          {c.total_orders > 0 && <span style={{ fontSize: F.size.sm, color: C.inkMuted, fontWeight: F.weight.medium, ...numeric }}>{c.total_orders} cmd</span>}
                        </label>
                      );
                    })}
                  </div>
                )}
              </div>

              {/* Send button */}
              <Btn fullWidth icon="send" loading={broadcastSending} disabled={broadcastSending || !broadcastMsg.trim() || broadcastSelected.length === 0}
                onClick={async () => {
                  if (!window.confirm(`Envoyer ce message à ${broadcastSelected.length} client(s) via WhatsApp ?`)) return;
                  setBroadcastSending(true); setBroadcastResult(null);
                  try {
                    const r = await api.broadcastWhatsApp(broadcastMsg.trim(), broadcastSelected);
                    setBroadcastResult({ ok: true, sent: r.sent, failed: r.failed, total: r.total, errors: r.errors });
                  } catch (err) {
                    setBroadcastResult({ ok: false, msg: err.message });
                  } finally { setBroadcastSending(false); }
                }}>
                {broadcastSending ? "Envoi en cours…" : `Envoyer à ${broadcastSelected.length} client(s)`}
              </Btn>

              {/* Result */}
              {broadcastResult && (
                <div style={{ marginTop: 12 }}>
                  {broadcastResult.ok ? (
                    <Callout tone="success" title={`Envoi terminé : ${broadcastResult.sent} envoyé(s) / ${broadcastResult.total} total`}>
                      {broadcastResult.failed > 0 && (
                        <div style={{ color: C.danger }}>
                          {broadcastResult.failed} échec(s)
                          {broadcastResult.errors?.length > 0 && (
                            <ul style={{ margin: "4px 0 0 16px", padding: 0 }}>
                              {broadcastResult.errors.map((e, i) => <li key={i}>{e.phone} : {e.error}</li>)}
                            </ul>
                          )}
                        </div>
                      )}
                    </Callout>
                  ) : (
                    <Callout tone="danger" title="Erreur">{broadcastResult.msg}</Callout>
                  )}
                </div>
              )}
            </Card>
          </div>
        )}

        {tab === "receipt" && (
          <div style={{ marginTop: 8 }}>
            <SectionHeader icon="receipt" title="Format des reçus" subtitle="Format par défaut pour l'impression des reçus PDF" />
            <Card>
              <div style={{ display: "flex", gap: 12, flexWrap: "wrap" }}>
                {[
                  { key: "ticket", icon: "ticket", label: "Ticket 80mm", desc: "Format ticket de caisse (80 mm de large)", img: "80×200 mm" },
                  { key: "a5", icon: "document", label: "A5", desc: "Format demi-page, compact et lisible", img: "148×210 mm" },
                  { key: "a4", icon: "document", label: "A4", desc: "Format pleine page standard", img: "210×297 mm" },
                ].map(f => {
                  const selected = receiptFormat === f.key;
                  return (
                    <button key={f.key} type="button" onClick={async () => {
                      setReceiptFormat(f.key);
                      try { await api.saveSettings({ receipt_format: f.key }); } catch {}
                    }}
                      style={{
                        flex: 1, minWidth: 160, padding: 16, borderRadius: R.lg, cursor: "pointer",
                        border: `1px solid ${selected ? C.navy : C.border}`,
                        background: selected ? C.navySoft : C.surface,
                        textAlign: "left",
                      }}>
                      <div className="icon-text" style={{ marginBottom: 6, color: selected ? C.navy : C.inkMuted }}>
                        <Icon name={f.icon} size={16} />
                        <span style={{ fontWeight: F.weight.semi, fontSize: F.size.md, color: selected ? C.navy : C.ink }}>{f.label}</span>
                      </div>
                      <p style={{ fontSize: F.size.sm, color: C.inkMuted, marginBottom: 3, lineHeight: 1.5 }}>{f.desc}</p>
                      <span style={{ fontSize: F.size.xs, color: C.inkFaint, ...numeric }}>{f.img}</span>
                      {selected && (
                        <div className="icon-text" style={{ marginTop: 8, fontSize: F.size.sm, fontWeight: F.weight.medium, color: C.navy }}>
                          <Icon name="check" size={13} />Sélectionné
                        </div>
                      )}
                    </button>
                  );
                })}
              </div>
            </Card>

            {/* ═══ RECEIPT FONT SIZES ═══ */}
            <SectionHeader
              icon="text"
              title="Tailles de police des reçus"
              subtitle="À l'impression, l'utilisateur choisit le profil Normal ou Grand"
              style={{ marginTop: 28 }}
            />

            {["normal", "grand"].map(profile => {
              const sizes = (receiptSizes && receiptSizes[profile]) || DEFAULT_RECEIPT_SIZES[profile];
              return (
                <Card key={profile} style={{ marginBottom: 16 }}>
                  <h3 className="icon-text" style={{ fontWeight: F.weight.semi, color: C.ink, fontSize: F.size.lg, marginBottom: 12 }}>
                    <Icon name="document" size={16} color={C.inkMuted} />
                    {profile === "normal" ? "Profil Normal" : "Profil Grand"}
                  </h3>
                  <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))", gap: 10 }}>
                    {Object.entries(RECEIPT_SIZE_LABELS).map(([key, label]) => (
                      <div key={key} style={{ display: "flex", alignItems: "center", gap: 8 }}>
                        <label style={{ flex: 1, fontSize: F.size.base, color: C.inkBody, fontWeight: F.weight.medium }}>{label}</label>
                        <input
                          className="field"
                          type="number" min="8" max="120" step="1"
                          value={sizes[key] || DEFAULT_RECEIPT_SIZES[profile][key]}
                          onChange={e => {
                            const val = Number(e.target.value);
                            setReceiptSizes(prev => {
                              const p = prev || {};
                              return { ...p, [profile]: { ...(p[profile] || DEFAULT_RECEIPT_SIZES[profile]), [key]: val } };
                            });
                          }}
                          style={{ width: 68, textAlign: "center" }}
                        />
                        <span style={{ fontSize: F.size.xs, color: C.inkFaint }}>pt</span>
                      </div>
                    ))}
                  </div>
                  <Btn
                    small variant="ghost" icon="undo"
                    style={{ marginTop: 10 }}
                    onClick={() => {
                      setReceiptSizes(prev => ({ ...(prev || {}), [profile]: { ...DEFAULT_RECEIPT_SIZES[profile] } }));
                    }}
                  >Réinitialiser ce profil</Btn>
                </Card>
              );
            })}

            <Btn fullWidth icon="save" loading={receiptSizesSaving} style={{ marginBottom: 12 }}
              onClick={async () => {
                setReceiptSizesSaving(true);
                try {
                  const config = receiptSizes || DEFAULT_RECEIPT_SIZES;
                  await api.saveSettings({ receipt_font_sizes: JSON.stringify(config) });
                  setReceiptSizesCache(config);
                  alert("Tailles de police enregistrées.");
                } catch (err) { alert("Erreur : " + err.message); }
                finally { setReceiptSizesSaving(false); }
              }}>
              {receiptSizesSaving ? "Sauvegarde…" : "Sauvegarder les tailles de police"}
            </Btn>
          </div>
        )}

        {tab === "branding" && (
          <div style={{ marginTop: 8 }}>
            <SectionHeader icon="palette" title="Personnalisation du site" subtitle="Identité affichée aux clients" />

            {/* Slogan */}
            <Card style={{ marginBottom: 16 }}>
              <SectionHeader icon="sparkle" title="Slogan" subtitle="Texte affiché sous le titre principal de la page d'accueil" />
              <input className="field" type="text" value={brandSlogan} onChange={e => setBrandSlogan(e.target.value)}
                placeholder="ex : Le pressing vient vers vous."
                style={{ marginBottom: 12 }} />
              <div style={{ background: C.surfaceAlt, border: `1px solid ${C.border}`, borderRadius: R.md, padding: "10px 12px" }}>
                <p style={{ fontSize: F.size.sm, color: C.inkMuted, marginBottom: 4 }}>Aperçu</p>
                <p style={{ fontSize: F.size.lg, fontWeight: F.weight.medium, fontStyle: "italic", color: C.ink }}>{brandSlogan || "—"}</p>
              </div>
            </Card>

            {/* Logo URL */}
            <Card style={{ marginBottom: 16 }}>
              <SectionHeader icon="image" title="Logo" subtitle="Affiché dans la barre de navigation, le hero et le pied de page" />
              <input className="field" type="text" value={brandLogoUrl} onChange={e => setBrandLogoUrl(e.target.value)}
                placeholder="ex : /logo-setal.svg ou https://…"
                style={{ marginBottom: 12 }} />
              {(brandLogoUrl || "/logo-setal.svg") && (
                <div style={{ background: C.surfaceAlt, border: `1px solid ${C.border}`, borderRadius: R.md, padding: 16, textAlign: "center" }}>
                  <p style={{ fontSize: F.size.sm, color: C.inkMuted, marginBottom: 8 }}>Aperçu</p>
                  <div style={{ background: C.navy, borderRadius: R.lg, padding: 16, display: "inline-block" }}>
                    <img src={brandLogoUrl || "/logo-setal.svg"} alt="Logo" style={{ height: 60, width: "auto" }}
                      onError={e => { e.target.style.display = "none"; }} />
                  </div>
                </div>
              )}
            </Card>

            {/* Téléphones */}
            <Card style={{ marginBottom: 16 }}>
              <SectionHeader icon="phone" title="Numéros de téléphone" subtitle="Affichés sur le site, les reçus et la page de suivi" />

              <div style={{ display: "grid", gap: 14 }}>
                <div>
                  <label className="icon-text" style={{ fontSize: F.size.base, fontWeight: F.weight.medium, color: C.inkBody, marginBottom: 5 }}>
                    <Icon name="phone" size={13} color={C.inkMuted} />Numéro principal (appels)
                  </label>
                  <input className="field" type="text" value={phoneMain} onChange={e => setPhoneMain(e.target.value)}
                    placeholder="+221 76 809 09 28" />
                  <p style={{ fontSize: F.size.sm, color: C.inkFaint, marginTop: 5 }}>Format affiché : +221 XX XXX XX XX</p>
                </div>

                <div>
                  <label className="icon-text" style={{ fontSize: F.size.base, fontWeight: F.weight.medium, color: C.inkBody, marginBottom: 5 }}>
                    <Icon name="chat" size={13} color={C.inkMuted} />Numéro WhatsApp
                  </label>
                  <input className="field" type="text" value={phoneWhatsapp} onChange={e => setPhoneWhatsapp(e.target.value)}
                    placeholder="221768090928" />
                  <p style={{ fontSize: F.size.sm, color: C.inkFaint, marginTop: 5 }}>Sans espaces ni « + » : 221XXXXXXXXX (utilisé pour les liens wa.me)</p>
                </div>

                <div>
                  <label className="icon-text" style={{ fontSize: F.size.base, fontWeight: F.weight.medium, color: C.inkBody, marginBottom: 5 }}>
                    <Icon name="mobile" size={13} color={C.inkMuted} />Numéros supplémentaires (optionnel)
                  </label>
                  <textarea className="field" value={phonesExtra} onChange={e => setPhonesExtra(e.target.value)}
                    placeholder={"Ex:\nCommercial: +221 77 123 45 67\nSAV: +221 78 987 65 43"}
                    rows={3}
                    style={{ resize: "vertical", lineHeight: 1.5 }} />
                  <p style={{ fontSize: F.size.sm, color: C.inkFaint, marginTop: 5 }}>Un numéro par ligne avec son libellé. Affichés dans le pied de page du site.</p>
                </div>
              </div>

              {/* Preview */}
              <div style={{ marginTop: 14, background: C.surfaceAlt, border: `1px solid ${C.border}`, borderRadius: R.md, padding: "10px 12px" }}>
                <p style={{ fontSize: F.size.sm, color: C.inkMuted, marginBottom: 8 }}>Aperçu</p>
                <div style={{ display: "flex", gap: 16, flexWrap: "wrap" }}>
                  <a className="icon-text" href={`tel:${phoneMain.replace(/\s/g, "")}`} style={{ color: C.navy, fontWeight: F.weight.medium, fontSize: F.size.base, textDecoration: "none", ...numeric }}>
                    <Icon name="phone" size={14} />{phoneMain}
                  </a>
                  <a className="icon-text" href={`https://wa.me/${phoneWhatsapp}`} style={{ color: C.success, fontWeight: F.weight.medium, fontSize: F.size.base, textDecoration: "none" }}>
                    <Icon name="chat" size={14} />WhatsApp
                  </a>
                </div>
                {phonesExtra && phonesExtra.trim().split("\n").filter(Boolean).map((line, i) => (
                  <div key={i} className="icon-text" style={{ fontSize: F.size.base, color: C.inkBody, marginTop: 5 }}>
                    <Icon name="mobile" size={13} color={C.inkMuted} />{line}
                  </div>
                ))}
              </div>
            </Card>

            {/* Save button */}
            <Btn fullWidth icon="save" loading={brandSaving}
              onClick={async () => {
                setBrandSaving(true);
                try {
                  await api.saveSettings({ slogan: brandSlogan, logo_url: brandLogoUrl, phone_main: phoneMain, phone_whatsapp: phoneWhatsapp, phones_extra: phonesExtra });
                  alert("Personnalisation enregistrée.");
                } catch (err) { alert("Erreur : " + err.message); }
                finally { setBrandSaving(false); }
              }}>
              {brandSaving ? "Sauvegarde…" : "Sauvegarder la personnalisation"}
            </Btn>
          </div>
        )}

        {tab === "clients" && (
          <div style={{ marginTop: 8 }}>
            <SectionHeader icon="users" title="Base clients" subtitle="Historique et fidélité de chaque client" />

            {/* Search bar */}
            <div style={{ marginBottom: 16, position: "relative" }}>
              <span style={{ position: "absolute", left: 10, top: "50%", transform: "translateY(-50%)", color: C.inkFaint, pointerEvents: "none" }}>
                <Icon name="search" size={15} />
              </span>
              <input
                className="field"
                type="text" value={clientSearch} placeholder="Rechercher par nom, téléphone ou adresse…"
                onChange={e => {
                  setClientSearch(e.target.value);
                  const val = e.target.value;
                  clearTimeout(window._clientSearchTimeout);
                  window._clientSearchTimeout = setTimeout(() => {
                    setClientsLoading(true);
                    api.adminGetClients(val).then(d => setClientsList(d || [])).catch(() => {}).finally(() => setClientsLoading(false));
                  }, 400);
                }}
                style={{ paddingLeft: 32 }}
              />
            </div>

            {/* Stats cards */}
            {!clientsLoading && clientsList.length > 0 && (
              <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(150px, 1fr))", gap: 10, marginBottom: 16 }}>
                <Stat icon="users" label="Clients total" value={clientsList.length} tone="navy" />
                <Stat icon="package" label="Commandes total" value={clientsList.reduce((s, c) => s + (c.total_orders || 0), 0)} />
                <Stat icon="gift" label="Clients avec points" value={clientsList.filter(c => c.loyalty_points > 0).length} tone="accent" />
                <Stat icon="money" label="CA total" unit="FCFA" value={clientsList.reduce((s, c) => s + Number(c.total_spent || 0), 0).toLocaleString()} tone="success" />
              </div>
            )}

            {/* Sort buttons */}
            <div style={{ display: "flex", gap: 6, marginBottom: 12, flexWrap: "wrap", alignItems: "center" }}>
              <span style={{ fontSize: F.size.sm, color: C.inkMuted, marginRight: 2 }}>Trier par</span>
              {[["total_orders", "Commandes", "package"], ["total_spent", "Dépenses", "money"], ["loyalty_points", "Points", "gift"], ["last_order_at", "Récent", "clock"]].map(([k, label, ic]) => {
                const selected = clientSort === k;
                return (
                  <button key={k} type="button" onClick={() => setClientSort(k)} className="icon-text" style={{
                    padding: "5px 11px", borderRadius: R.md,
                    border: `1px solid ${selected ? C.navy : C.border}`,
                    background: selected ? C.navySoft : C.surface,
                    color: selected ? C.navy : C.inkBody,
                    fontWeight: F.weight.medium, fontSize: F.size.sm, cursor: "pointer",
                  }}>
                    <Icon name={ic} size={13} />{label}
                  </button>
                );
              })}
            </div>

            {/* Client detail modal */}
            {clientDetail && (
              <Modal
                title={clientDetail.name || "Client"}
                icon="user"
                width={620}
                onClose={() => { setClientDetail(null); setClientOrders([]); }}
              >
                {/* Client info cards */}
                <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(150px, 1fr))", gap: 10, marginBottom: 16 }}>
                  <Stat icon="phone" label="Téléphone" value={<span style={{ fontSize: F.size.md }}>{clientDetail.phone}</span>} />
                  <Stat icon="location" label="Adresse" value={<span style={{ fontSize: F.size.md }}>{clientDetail.address || "—"}</span>} />
                  <Stat icon="package" label="Commandes" value={clientDetail.total_orders} tone="navy" />
                  <Stat icon="money" label="Total dépensé" unit="F" value={Number(clientDetail.total_spent).toLocaleString()} tone="success" />
                  <Stat icon="gift" label="Points fidélité" unit="pts" value={clientDetail.loyalty_points} tone="accent" />
                  <Stat icon="calendar" label="Client depuis" value={<span style={{ fontSize: F.size.md }}>{clientDetail.first_order_at ? new Date(clientDetail.first_order_at).toLocaleDateString("fr-FR") : "—"}</span>} />
                </div>

                {/* Orders history */}
                <SectionHeader icon="list" title="Historique des commandes" />
                {clientOrdersLoading ? <Spinner /> : clientOrders.length === 0 ? (
                  <EmptyState compact icon="package" title="Aucune commande" />
                ) : (
                  <div className="tbl-wrap" style={{ maxHeight: 300, overflowY: "auto" }}>
                    <table className="tbl">
                      <thead>
                        <tr><th>Réf</th><th>Date</th><th>Statut</th><th className="num">Montant</th></tr>
                      </thead>
                      <tbody>
                        {clientOrders.map(o => (
                          <tr key={o.id}>
                            <td>
                              <Ref>{o.id}</Ref>
                              {o.items && <div style={{ fontSize: F.size.xs, color: C.inkFaint, marginTop: 2 }}>{o.items}</div>}
                            </td>
                            <td style={{ color: C.inkMuted, whiteSpace: "nowrap", ...numeric }}>{new Date(o.created_at).toLocaleDateString("fr-FR")}</td>
                            <td><Badge status={o.status} small /></td>
                            <td className="num">
                              {o.price && <div style={{ fontWeight: F.weight.semi, color: C.ink }}>{Number(o.price).toLocaleString()} F</div>}
                              {Number(o.loyalty_discount) > 0 && (
                                <div style={{ fontSize: F.size.xs, color: C.success }}>−{Number(o.loyalty_discount).toLocaleString()} F fidélité</div>
                              )}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </Modal>
            )}

            {/* Clients list */}
            {clientsLoading ? <Spinner /> : clientsList.length === 0 ? (
              <Card padded={false}>
                <EmptyState
                  icon="users"
                  title={clientSearch ? "Aucun client trouvé" : "Aucun client"}
                  description={clientSearch ? "Essayez un autre nom, téléphone ou adresse." : "Les clients apparaîtront ici dès la première commande."}
                />
              </Card>
            ) : (
              <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                {[...clientsList].sort((a, b) => {
                  if (clientSort === "total_orders") return (b.total_orders || 0) - (a.total_orders || 0);
                  if (clientSort === "total_spent") return Number(b.total_spent || 0) - Number(a.total_spent || 0);
                  if (clientSort === "loyalty_points") return (b.loyalty_points || 0) - (a.loyalty_points || 0);
                  if (clientSort === "last_order_at") return new Date(b.last_order_at || 0) - new Date(a.last_order_at || 0);
                  return 0;
                }).map((c, i) => (
                  <Card key={c.phone} hover style={{ padding: "12px 14px", cursor: "pointer" }}
                    onClick={() => {
                      setClientDetail(c);
                      setClientOrdersLoading(true);
                      api.adminGetClientOrders(c.phone).then(d => setClientOrders(d || [])).catch(() => {}).finally(() => setClientOrdersLoading(false));
                    }}>
                    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 12 }}>
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ display: "flex", alignItems: "center", gap: 7, marginBottom: 4, flexWrap: "wrap" }}>
                          <span style={{ fontWeight: F.weight.semi, color: C.ink, fontSize: F.size.md }}>{c.name || "Sans nom"}</span>
                          {c.loyalty_points > 0 && (
                            <Chip small tone="navy" icon="gift">{c.loyalty_points} pts</Chip>
                          )}
                          {c.total_orders >= 10 && (
                            <Chip small tone="warning" icon="star">Fidèle</Chip>
                          )}
                        </div>
                        <div style={{ display: "flex", gap: 12, flexWrap: "wrap", fontSize: F.size.sm, color: C.inkMuted }}>
                          <span className="icon-text" style={numeric}><Icon name="mobile" size={13} />{c.phone}</span>
                          {c.address && <span className="icon-text"><Icon name="location" size={13} />{c.address}</span>}
                        </div>
                      </div>
                      <div style={{ textAlign: "right", minWidth: 90, flexShrink: 0, ...numeric }}>
                        <div style={{ fontWeight: F.weight.bold, color: C.ink, fontSize: F.size.lg }}>{c.total_orders}</div>
                        <div style={{ fontSize: F.size.xs, color: C.inkFaint }}>commandes</div>
                        <div style={{ fontSize: F.size.sm, fontWeight: F.weight.semi, color: C.success }}>{Number(c.total_spent).toLocaleString()} F</div>
                      </div>
                    </div>
                  </Card>
                ))}
              </div>
            )}
          </div>
        )}

        {tab === "loyalty" && (
          <div style={{ marginTop: 8 }}>
            <SectionHeader icon="gift" title="Programme de fidélité" subtitle="Réductions automatiques accordées aux clients réguliers" />
            {loyaltyLoading ? <Spinner /> : (
              <div>
                <Card style={{ marginBottom: 16 }}>
                  <SectionHeader
                    icon="star"
                    title="Activer le programme"
                    subtitle="Les clients accumulent 1 point par tranche de 1 000 FCFA"
                    actions={
                      <Toggle
                        checked={!!loyaltyConfig.enabled}
                        onChange={() => setLoyaltyConfig(c => ({ ...c, enabled: !c.enabled }))}
                        label={loyaltyConfig.enabled ? "Activé" : "Désactivé"}
                      />
                    }
                  />
                  {!loyaltyConfig.enabled && (
                    <Callout tone="warning">
                      Le programme est désactivé. Les clients ne bénéficieront pas de réductions automatiques.
                    </Callout>
                  )}
                </Card>

                <Card style={{ marginBottom: 16 }}>
                  <SectionHeader
                    icon="percent"
                    title="Paliers de réduction"
                    subtitle="La réduction s'applique automatiquement sur la commande suivante"
                  />
                  <div style={{ display: "flex", flexDirection: "column", gap: 10, marginBottom: 12 }}>
                    {loyaltyConfig.thresholds.map((t, i) => (
                      <div key={i} style={{ display: "flex", gap: 8, alignItems: "flex-end" }}>
                        <div style={{ flex: 1 }}>
                          <label style={{ display: "block", fontSize: F.size.sm, color: C.inkMuted, fontWeight: F.weight.medium, marginBottom: 4 }}>Points</label>
                          <input className="field" type="number" value={t.points} onChange={e => {
                            const arr = [...loyaltyConfig.thresholds];
                            arr[i] = { ...arr[i], points: parseInt(e.target.value) || 0 };
                            setLoyaltyConfig(c => ({ ...c, thresholds: arr }));
                          }} />
                        </div>
                        <div style={{ flex: 1 }}>
                          <label style={{ display: "block", fontSize: F.size.sm, color: C.inkMuted, fontWeight: F.weight.medium, marginBottom: 4 }}>Réduction (%)</label>
                          <input className="field" type="number" value={t.discount} onChange={e => {
                            const arr = [...loyaltyConfig.thresholds];
                            arr[i] = { ...arr[i], discount: parseInt(e.target.value) || 0 };
                            setLoyaltyConfig(c => ({ ...c, thresholds: arr }));
                          }} />
                        </div>
                        <IconBtn icon="delete" title="Supprimer ce palier" danger variant="outline" style={{ marginBottom: 3 }} onClick={() => {
                          setLoyaltyConfig(c => ({ ...c, thresholds: c.thresholds.filter((_, j) => j !== i) }));
                        }} />
                      </div>
                    ))}
                  </div>
                  <Btn fullWidth variant="ghost" icon="add"
                    onClick={() => setLoyaltyConfig(c => ({ ...c, thresholds: [...c.thresholds, { points: 0, discount: 0 }] }))}>
                    Ajouter un palier
                  </Btn>
                </Card>

                {loyaltyConfig.thresholds.length > 0 && (
                  <Card style={{ marginBottom: 16 }}>
                    <SectionHeader icon="show" title="Aperçu" />
                    <div className="tbl-wrap">
                      <table className="tbl">
                        <thead>
                          <tr><th className="num">Points</th><th>Équivalent dépensé</th><th className="num">Réduction</th></tr>
                        </thead>
                        <tbody>
                          {[...loyaltyConfig.thresholds].sort((a, b) => a.points - b.points).map((t, i) => (
                            <tr key={i}>
                              <td className="num" style={{ fontWeight: F.weight.semi, color: C.ink }}>{t.points}</td>
                              <td style={{ color: C.inkMuted, ...numeric }}>{(t.points * 1000).toLocaleString()} FCFA</td>
                              <td className="num" style={{ fontWeight: F.weight.semi, color: C.success }}>−{t.discount} %</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </Card>
                )}

                <Btn fullWidth icon="save" loading={loyaltySaving} onClick={async () => {
                  setLoyaltySaving(true);
                  try {
                    const sorted = [...loyaltyConfig.thresholds].filter(t => t.points > 0 && t.discount > 0).sort((a, b) => a.points - b.points);
                    await api.saveLoyaltyConfig({ enabled: loyaltyConfig.enabled, thresholds: sorted });
                    setLoyaltyConfig(c => ({ ...c, thresholds: sorted }));
                    alert("Configuration fidélité enregistrée !");
                  } catch (err) { alert("Erreur : " + err.message); }
                  finally { setLoyaltySaving(false); }
                }}>
                  {loyaltySaving ? "Enregistrement…" : "Enregistrer la configuration"}
                </Btn>
              </div>
            )}
          </div>
        )}

        {tab === "parrainage" && (
          <div style={{ marginTop: 8 }}>
            <SectionHeader icon="handshake" title="Programme de parrainage" subtitle="Codes partagés par les clients et retombées" />
            {referralLoading ? <Spinner /> : !referralStats ? (
              <Card padded={false}>
                <EmptyState icon="handshake" title="Aucune donnée disponible" description="Les statistiques de parrainage apparaîtront ici." />
              </Card>
            ) : (
              <div>
                {/* ─── KPI global ─── */}
                <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(170px, 1fr))", gap: 12, marginBottom: 16 }}>
                  {[
                    ["ticket", "Codes générés", referralStats.global.total_codes_generated, "", "navy"],
                    ["target", "Parrainages réussis", referralStats.global.total_referral_orders, "commandes", "success"],
                    ["money", "CA parrainé", `${(referralStats.global.total_referral_revenue || 0).toLocaleString()}`, "FCFA", "accent"],
                    ["gift", "Réductions offertes", `${(referralStats.global.total_discount_given || 0).toLocaleString()}`, "FCFA", "info"],
                  ].map(([icon, label, value, unit, tone], i) => (
                    <Stat key={i} icon={icon} label={label} value={value} unit={unit} tone={tone} />
                  ))}
                </div>

                {/* ─── Configuration récompense ─── */}
                <Card style={{ marginBottom: 16 }}>
                  <SectionHeader icon="settings" title="Configuration" />
                  <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
                    <div>
                      <Toggle
                        checked={!!promoConfig.referral_enabled}
                        onChange={v => setPromoConfig({ ...promoConfig, referral_enabled: v })}
                        label="Parrainage activé"
                      />
                      <p style={{ fontSize: F.size.sm, color: C.inkFaint, marginTop: 4 }}>Les clients reçoivent un code unique à partager.</p>
                    </div>
                    <div>
                      <label style={{ display: "block", fontSize: F.size.base, fontWeight: F.weight.medium, color: C.inkBody, marginBottom: 5 }}>Récompense parrain (FCFA)</label>
                      <input className="field" type="number" value={promoConfig.referral_reward || 2000} onChange={e => setPromoConfig({ ...promoConfig, referral_reward: parseInt(e.target.value) || 0 })} placeholder="2000" />
                      <p style={{ fontSize: F.size.sm, color: C.inkFaint, marginTop: 5 }}>Montant crédité en points au parrain à chaque commande réussie d'un filleul.</p>
                    </div>
                    <div>
                      <Btn icon="save" onClick={async () => {
                        try { await api.savePromoConfig(promoConfig); alert("Configuration enregistrée."); }
                        catch (e) { alert("Erreur : " + (e.message || "Erreur")); }
                      }}>Sauvegarder</Btn>
                    </div>
                  </div>
                </Card>

                {/* ─── Top parrains ─── */}
                <Card>
                  <SectionHeader icon="trophy" title="Meilleurs parrains" />
                  {referralStats.topReferrers.length === 0 ? (
                    <EmptyState compact icon="handshake" title="Aucun parrainage pour le moment" />
                  ) : (
                    <div className="tbl-wrap">
                      <table className="tbl">
                        <thead>
                          <tr>
                            <th style={{ width: 34 }}>#</th>
                            <th>Parrain</th>
                            <th>Code</th>
                            <th className="num">Filleuls</th>
                            <th className="num">CA généré</th>
                            <th className="num">Points</th>
                          </tr>
                        </thead>
                        <tbody>
                          {referralStats.topReferrers.map((r, i) => (
                            <tr key={r.code}>
                              <td style={{ color: C.inkFaint, ...numeric }}>
                                {i < 3 ? <Icon name="medal" size={14} color={C.amber} /> : i + 1}
                              </td>
                              <td>
                                <div style={{ fontWeight: F.weight.medium, color: C.ink }}>{r.client}</div>
                                <div style={{ fontSize: F.size.xs, color: C.inkFaint, ...numeric }}>{r.phone}</div>
                              </td>
                              <td><Ref>{r.code}</Ref></td>
                              <td className="num" style={{ fontWeight: F.weight.semi, color: r.referrals > 0 ? C.success : C.inkFaint }}>{r.referrals}</td>
                              <td className="num" style={{ fontWeight: F.weight.medium, color: C.ink }}>{r.generated_revenue.toLocaleString()} F</td>
                              <td className="num"><Chip small tone="warning">{r.points} pts</Chip></td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </Card>
              </div>
            )}
          </div>
        )}

        {tab === "promo" && (
          <div style={{ marginTop: 8 }}>
            <SectionHeader icon="ticket" title="Codes promo & parrainage" subtitle="Réductions ponctuelles et codes de parrainage" />

            {/* Config toggles */}
            {promoConfigLoading ? <Spinner /> : (
              <Card style={{ marginBottom: 16 }}>
                <SectionHeader icon="settings" title="Configuration" />
                {[
                  ["promo_enabled", "Codes promo", "Permettre l'utilisation de codes promo lors des commandes"],
                  ["referral_enabled", "Parrainage", "Les clients reçoivent un code de parrainage unique"],
                  ["welcome_enabled", "Message de bienvenue WhatsApp", "Envoyer un message WhatsApp aux nouveaux clients"],
                ].map(([key, label, desc]) => (
                  <div key={key} style={{ marginBottom: 12, paddingBottom: 12, borderBottom: `1px solid ${C.border}` }}>
                    <Toggle checked={!!promoConfig[key]} onChange={() => setPromoConfig(c => ({ ...c, [key]: !c[key] }))} label={label} />
                    <p style={{ fontSize: F.size.sm, color: C.inkFaint, marginTop: 4 }}>{desc}</p>
                  </div>
                ))}
                {promoConfig.welcome_enabled && (
                  <div style={{ marginBottom: 12 }}>
                    <label style={{ display: "block", fontSize: F.size.base, fontWeight: F.weight.medium, color: C.inkBody, marginBottom: 5 }}>Message de bienvenue</label>
                    <textarea className="field" value={promoConfig.welcome_message || ""} onChange={e => setPromoConfig(c => ({ ...c, welcome_message: e.target.value }))}
                      rows={3} placeholder="Bienvenue chez Setal Express ! Merci pour votre confiance…"
                      style={{ resize: "vertical", lineHeight: 1.5 }} />
                    <label style={{ display: "block", fontSize: F.size.base, fontWeight: F.weight.medium, color: C.inkBody, marginBottom: 5, marginTop: 10 }}>Code promo de bienvenue</label>
                    <input className="field" value={promoConfig.welcome_promo || ""} onChange={e => setPromoConfig(c => ({ ...c, welcome_promo: e.target.value }))}
                      placeholder="BIENVENUE30" />
                  </div>
                )}
                {promoConfig.referral_enabled && (
                  <div style={{ marginBottom: 12 }}>
                    <label style={{ display: "block", fontSize: F.size.base, fontWeight: F.weight.medium, color: C.inkBody, marginBottom: 5 }}>Récompense parrainage (FCFA)</label>
                    <input className="field" type="number" value={promoConfig.referral_reward || 2000} onChange={e => setPromoConfig(c => ({ ...c, referral_reward: parseInt(e.target.value) || 0 }))} />
                    <p style={{ fontSize: F.size.sm, color: C.inkFaint, marginTop: 5 }}>Points bonus accordés au parrain (1 point = 1 000 FCFA).</p>
                  </div>
                )}
                <Btn fullWidth icon="save" loading={promoSaving} onClick={async () => {
                  setPromoSaving(true);
                  try { await api.savePromoConfig(promoConfig); alert("Configuration sauvegardée !"); }
                  catch (err) { alert("Erreur : " + err.message); }
                  finally { setPromoSaving(false); }
                }}>
                  {promoSaving ? "Enregistrement…" : "Enregistrer la configuration"}
                </Btn>
              </Card>
            )}

            {/* Promo codes CRUD */}
            <SectionHeader
              icon="tag"
              title={`Codes promo (${promoCodes.length})`}
              actions={
                <Btn small icon="add" onClick={() => {
                  setEditPromo(null); setPCode(""); setPType("percentage"); setPValue(""); setPMaxUses(""); setPMinAmount(""); setPExpires(""); setPromoActive(true);
                  setShowPromoForm(true);
                }}>Nouveau code</Btn>
              }
            />

            {promoLoading ? <Spinner /> : promoCodes.length === 0 ? (
              <Card padded={false}>
                <EmptyState
                  icon="ticket"
                  title="Aucun code promo"
                  description="Créez un premier code de réduction pour vos clients."
                  action={
                    <Btn small icon="add" onClick={() => {
                      setEditPromo(null); setPCode(""); setPType("percentage"); setPValue(""); setPMaxUses(""); setPMinAmount(""); setPExpires(""); setPromoActive(true);
                      setShowPromoForm(true);
                    }}>Nouveau code</Btn>
                  }
                />
              </Card>
            ) : (
              <div className="tbl-wrap">
                <table className="tbl">
                  <thead>
                    <tr>
                      <th>Code</th>
                      <th>Réduction</th>
                      <th className="num">Utilisations</th>
                      <th>Expiration</th>
                      <th>Statut</th>
                      <th style={{ width: 76 }}>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {promoCodes.map(p => (
                      <tr key={p.id} style={{ opacity: p.active ? 1 : 0.55 }}>
                        <td><Ref>{p.code}</Ref></td>
                        <td style={{ color: C.inkBody, ...numeric }}>
                          {p.type === "percentage" ? `−${p.value} %` : `−${parseInt(p.value).toLocaleString()} FCFA`}
                          {p.min_amount > 0 && (
                            <span style={{ color: C.inkFaint }}> · min {parseInt(p.min_amount).toLocaleString()} FCFA</span>
                          )}
                        </td>
                        <td className="num">{p.used_count || 0}/{p.max_uses || "illimité"}</td>
                        <td style={{ color: C.inkMuted, ...numeric }}>
                          {p.expires_at ? new Date(p.expires_at).toLocaleDateString("fr-FR") : "—"}
                        </td>
                        <td><Chip small tone={p.active ? "success" : "danger"}>{p.active ? "Actif" : "Inactif"}</Chip></td>
                        <td>
                          <div style={{ display: "flex", gap: 2 }}>
                            <IconBtn icon="edit" title="Modifier" onClick={() => {
                              setEditPromo(p); setPCode(p.code); setPType(p.type); setPValue(p.value); setPMaxUses(p.max_uses || ""); setPMinAmount(p.min_amount || ""); setPExpires(p.expires_at ? p.expires_at.split("T")[0] : ""); setPromoActive(p.active);
                              setShowPromoForm(true);
                            }} />
                            <IconBtn icon="delete" title="Supprimer" danger onClick={async () => {
                              if (!window.confirm(`Supprimer le code "${p.code}" ?`)) return;
                              try { await api.adminDeletePromoCode(p.id); setPromoCodes(arr => arr.filter(x => x.id !== p.id)); } catch (err) { alert("Erreur : " + err.message); }
                            }} />
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}

            {/* Promo Form Modal */}
            {showPromoForm && (
              <Modal
                title={editPromo ? "Modifier le code" : "Nouveau code promo"}
                icon="ticket"
                onClose={() => setShowPromoForm(false)}
              >
                <form onSubmit={async e => {
                    e.preventDefault(); setPromoFormSaving(true);
                    try {
                      const data = { code: pCode.toUpperCase(), type: pType, value: parseFloat(pValue), active: promoActive };
                      if (pMaxUses) data.max_uses = parseInt(pMaxUses);
                      if (pMinAmount) data.min_amount = parseFloat(pMinAmount);
                      if (pExpires) data.expires_at = pExpires;
                      if (editPromo) {
                        const updated = await api.adminUpdatePromoCode(editPromo.id, data);
                        setPromoCodes(arr => arr.map(x => x.id === editPromo.id ? updated : x));
                      } else {
                        const created = await api.adminCreatePromoCode(data);
                        setPromoCodes(arr => [created, ...arr]);
                      }
                      setShowPromoForm(false);
                    } catch (err) { alert("Erreur : " + err.message); }
                    finally { setPromoFormSaving(false); }
                  }}>
                  <div style={{ marginBottom: 14 }}>
                    <label style={{ display: "block", fontSize: F.size.base, fontWeight: F.weight.medium, color: C.inkBody, marginBottom: 5 }}>
                      Code <span style={{ color: C.danger }} aria-hidden="true">*</span>
                    </label>
                    <input className="field" value={pCode} onChange={e => setPCode(e.target.value.toUpperCase())} required placeholder="ex : PROMO20"
                      style={{ fontFamily: F.mono, textTransform: "uppercase" }} />
                  </div>
                  <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
                    <Select label="Type" value={pType} onChange={v => setPType(v)}>
                      <option value="percentage">Pourcentage (%)</option>
                      <option value="fixed">Montant fixe (FCFA)</option>
                    </Select>
                    <Input label="Valeur" required type="number" value={pValue} onChange={setPValue} placeholder={pType === "percentage" ? "20" : "5000"} />
                  </div>
                  <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
                    <Input label="Utilisations max" type="number" value={pMaxUses} onChange={setPMaxUses} placeholder="Illimité" />
                    <Input label="Montant min (FCFA)" type="number" value={pMinAmount} onChange={setPMinAmount} placeholder="0" />
                  </div>
                  <Input label="Date d'expiration" type="date" value={pExpires} onChange={setPExpires} />
                  <div style={{ marginBottom: 16 }}>
                    <Toggle checked={promoActive} onChange={() => setPromoActive(!promoActive)} label="Code actif" />
                  </div>
                  <div style={{ display: "flex", gap: 8, justifyContent: "flex-end" }}>
                    <Btn variant="ghost" onClick={() => setShowPromoForm(false)}>Annuler</Btn>
                    <Btn type="submit" icon="save" loading={promoFormSaving}>{editPromo ? "Modifier" : "Créer"}</Btn>
                  </div>
                </form>
              </Modal>
            )}
          </div>
        )}

        {tab === "backup" && (
          <div style={{ marginTop: 8 }}>
            <SectionHeader icon="save" title="Sauvegarde" subtitle="Export complet de la base de données" />
            <Card padded={false}>
              <EmptyState
                icon="download"
                title="Exporter toutes les données"
                description="Téléchargez une sauvegarde complète au format JSON : commandes, utilisateurs, services, tarifs et journal."
                action={
                  <Btn icon="download" loading={backupLoading} onClick={handleBackup}>
                    {backupLoading ? "Exportation en cours…" : "Télécharger la sauvegarde"}
                  </Btn>
                }
              />
            </Card>
          </div>
        )}
        </div>{/* .admin-inner */}
        </div>{/* .admin-main */}
      </div>{/* .admin-shell */}
    </div>
  );
}

// ─── Review Page (Client) ────────────────────────────────────────────────────

export default Admin;
